import React, { useState, useEffect, useRef, forwardRef, useMemo, useCallback } from 'react';
import { Card, Steps, Form, Input, Select, Button, InputNumber, Switch, Descriptions, Tag, Collapse, message, Tooltip, Modal, Drawer, Avatar, Spin, Checkbox, Alert } from 'antd';
import { FilePdfOutlined, FileWordOutlined, FilePptOutlined, FileExcelOutlined, FileMarkdownOutlined, FileTextOutlined, FileImageOutlined, PlusOutlined, DeleteOutlined, InfoCircleOutlined, SendOutlined, UserOutlined, RobotOutlined, CheckCircleOutlined, CloseCircleOutlined, GithubOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useThemeStyles, useTheme } from '../context/ThemeContext';
import { formatCollectionNameWithUser } from '../utils/collectionName';
import { FieldLabel as FieldTooltip } from '../components/FieldTooltip';
import {
  applyCollectionCheckResult,
  interpretCollectionCheckResponse,
} from '../utils/collectionCheck';
import {
  PROJECT_DESCRIPTION_FORM_RULES,
  PROJECT_DESCRIPTION_MIN_LENGTH,
  PROJECT_DESCRIPTION_MAX_LENGTH,
} from '../constants/projectDescription';
import { colors } from '../theme';
import ProviderLogo from '../components/ProviderLogo';
import RetrieverSearchTypeSelect from '../components/RetrieverSearchTypeSelect';
import {
  DEFAULT_RETRIEVER_SEARCH_TYPE,
  retrieverSearchTypeLabel,
} from '../constants/retrieverSearchTypes';
import { mergeLlmProvidersForUi } from '../utils/llmProviders';
import { formatKnowledgeSourceVerified } from '../utils/successMessages';
import {
  ERROR_CONTEXT,
  humanizeApiError,
  humanizeConfluenceError,
} from '../utils/errorMessages';

const { TextArea } = Input;

const S3_REGION_OPTIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'af-south-1',
  'ap-east-1',
  'ap-south-1',
  'ap-south-2',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-southeast-3',
  'ap-southeast-4',
  'ap-northeast-1',
  'ap-northeast-2',
  'ap-northeast-3',
  'ca-central-1',
  'ca-west-1',
  'eu-central-1',
  'eu-central-2',
  'eu-west-1',
  'eu-west-2',
  'eu-west-3',
  'eu-north-1',
  'eu-south-1',
  'eu-south-2',
  'il-central-1',
  'me-south-1',
  'me-central-1',
  'sa-east-1',
];

const STEP_KEYS = {
  PROJECT_DETAILS: 'project_details',
  KNOWLEDGE_SOURCE: 'knowledge_source',
  LLM: 'llm',
  EMBEDDING: 'embedding',
  VECTOR_STORE: 'vector_store',
  GRAPH_RAG: 'graph_rag',
  RETRIEVER: 'retriever',
  GUARDRAILS: 'guardrails',
  REVIEW: 'review',
};

const KNOWLEDGE_PROVIDER = {
  S3: 's3',
  GITHUB: 'github',
  CONFLUENCE: 'confluence',
  JIRA: 'jira',
  AZURE_WIKI: 'azure_wiki',
};

const DEFAULT_JIRA_BASE_URL = 'https://thbs.atlassian.net';

const isProjectDescriptionValid = (description) => {
  const text = String(description ?? '').trim();
  return (
    text.length >= PROJECT_DESCRIPTION_MIN_LENGTH
    && text.length <= PROJECT_DESCRIPTION_MAX_LENGTH
  );
};

const isStepCompleteByKey = (stepKey, values, ctx) => {
  switch (stepKey) {
    case STEP_KEYS.PROJECT_DETAILS:
      return !!(
        values?.group_id
        && String(values?.name ?? '').trim()
        && isProjectDescriptionValid(values?.description)
      );
    case STEP_KEYS.KNOWLEDGE_SOURCE:
      if (ctx.knowledgeProvider === KNOWLEDGE_PROVIDER.S3) return ctx.s3Verified;
      if (ctx.knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE) {
        return ctx.confluenceVerified;
      }
      if (ctx.knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB) return ctx.githubVerified;
      if (ctx.knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA) {
        return ctx.jiraVerified;
      }
      if (ctx.knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI) {
        return ctx.azureWikiVerified;
      }
      return false;
    case STEP_KEYS.LLM:
      return !!(
        values?.config?.llm?.provider
        && values?.config?.llm?.model
      );
    case STEP_KEYS.EMBEDDING:
      return !!(
        values?.config?.embedding?.model
        && values?.config?.embedding?.model_name
      );
    case STEP_KEYS.VECTOR_STORE: {
      const coll = String(values?.config?.vector_store?.collection_name ?? '').trim();
      if (!values?.config?.vector_store?.type || !coll) return false;
      if (ctx.showCollectionStatus && coll) {
        return ctx.collectionAvailable && !ctx.collectionConnectionError;
      }
      return true;
    }
    case STEP_KEYS.GRAPH_RAG:
      if (!ctx.graphragEnabled) return true;
      return !!String(values?.config?.graphrag?.ingest_endpoint ?? '').trim();
    case STEP_KEYS.RETRIEVER:
      return !!values?.config?.retriever?.search_type;
    case STEP_KEYS.GUARDRAILS:
      return true;
    case STEP_KEYS.REVIEW:
      return true;
    default:
      return false;
  }
};

const getStepValidateFieldNames = (stepKey, knowledgeProvider, graphragEnabled) => {
  switch (stepKey) {
    case STEP_KEYS.PROJECT_DETAILS:
      return ['name', 'group_id', 'description'];
    case STEP_KEYS.KNOWLEDGE_SOURCE:
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.S3) {
        return [['config', 's3', 'bucket'], ['config', 's3', 'region']];
      }
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB) {
        return [
          ['config', 'github', 'github_base_url'],
          ['config', 'github', 'pat'],
        ];
      }
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE) {
        return [
          ['config', 'confluence', 'base_url'],
          ['config', 'confluence', 'email'],
          ['config', 'confluence', 'api_token'],
        ];
      }
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA) {
        return [
          ['config', 'jira', 'jira_base_url'],
          ['config', 'jira', 'jira_user_email'],
          ['config', 'jira', 'jira_api_token'],
        ];
      }
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI) {
        return [
          ['config', 'azure_wiki', 'organization_url'],
          ['config', 'azure_wiki', 'pat'],
        ];
      }
      return [];
    case STEP_KEYS.LLM:
      return [['config', 'llm', 'provider'], ['config', 'llm', 'model']];
    case STEP_KEYS.EMBEDDING:
      return [['config', 'embedding', 'model'], ['config', 'embedding', 'model_name']];
    case STEP_KEYS.VECTOR_STORE:
      return [
        ['config', 'vector_store', 'type'],
        ['config', 'vector_store', 'collection_name'],
      ];
    case STEP_KEYS.GRAPH_RAG:
      return graphragEnabled ? [['config', 'graphrag', 'ingest_endpoint']] : [];
    case STEP_KEYS.RETRIEVER:
      return [['config', 'retriever', 'search_type']];
    default:
      return [];
  }
};

/**
 * Ant Design Form control: receives `value` / `onChange` from Form.Item.
 * Matches Knowledge Source card styling.
 */
const FormTilePicker = forwardRef(function FormTilePicker(
  { value, onChange, options, themeStyles, colors, loading, afterChange, columns },
  ref,
) {
  const handleSelect = (opt) => {
    if (loading || opt.disabled) return;
    onChange(opt.value);
    afterChange?.(opt.value, opt);
  };

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref}>
      <div
        style={{
          display: columns ? 'grid' : 'flex',
          flexWrap: columns ? undefined : 'wrap',
          gridTemplateColumns: columns ? `repeat(${columns}, minmax(0, 1fr))` : undefined,
          gap: 12,
          marginBottom: 12,
        }}
      >
        {options.map((opt) => {
          const active = value === opt.value;
          const disabled = Boolean(opt.disabled) || loading;
          return (
            <button
              key={String(opt.value)}
              type="button"
              disabled={disabled}
              onClick={() => handleSelect(opt)}
              style={{
                flex: columns ? undefined : '1 1 140px',
                width: columns ? '100%' : undefined,
                minWidth: columns ? 0 : undefined,
                minHeight: 100,
                padding: '14px 16px',
                borderRadius: 10,
                textAlign: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.65 : 1,
                border: active && !disabled
                  ? `2px solid ${themeStyles.borderAccent}`
                  : `1px solid ${themeStyles.borderColor}`,
                background: active && !disabled ? themeStyles.elevatedBg : themeStyles.inputBg,
                color: themeStyles.textPrimary,
                boxShadow: active && !disabled ? themeStyles.shadowSm : 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s, opacity 0.2s',
              }}
            >
              <div
                style={{
                  color: active && !disabled ? colors.primary : themeStyles.textSecondary,
                  marginBottom: 8,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {opt.icon}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{opt.title}</div>
            </button>
          );
        })}
      </div>
      {selected?.description ? (
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 13,
            color: themeStyles.textSecondary,
            lineHeight: 1.55,
          }}
        >
          {selected.description}
        </p>
      ) : null}
    </div>
  );
});

const CreateProject = () => {
  const navigate = useNavigate();
  const { token, isAdmin, isSuperAdmin } = useAuth();
  const themeStyles = useThemeStyles();
  const { theme } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [form] = Form.useForm();
  const watchedFormValues = Form.useWatch([], form);
  const [selectedInputGuards, setSelectedInputGuards] = useState([]);
  const [selectedPreEmbeddingGuards, setSelectedPreEmbeddingGuards] = useState([]);
  const [selectedContextGuards, setSelectedContextGuards] = useState([]);
  const [selectedOutputGuards, setSelectedOutputGuards] = useState([]);
  
  // Watch guardrails enabled state from form
  const guardrailsEnabled = Form.useWatch(['config', 'guardrails', 'enabled'], form) ?? false;
  const retrieverSearchType = Form.useWatch(['config', 'retriever', 'search_type'], form) ?? DEFAULT_RETRIEVER_SEARCH_TYPE;
  const selectedEmbeddingProvider = Form.useWatch(['config', 'embedding', 'model'], form) ?? 'openai';
  const graphragEnabled = Form.useWatch(['config', 'graphrag', 'enabled'], form) ?? false;
  const projectName = Form.useWatch('name', form);
  const groupId = Form.useWatch('group_id', form);
  const collectionNameField = Form.useWatch(['config', 'vector_store', 'collection_name'], form);
  const [collectionName, setCollectionName] = useState('');
  const [checkingCollection, setCheckingCollection] = useState(false);
  const [showCollectionStatus, setShowCollectionStatus] = useState(false);
  const [collectionAvailable, setCollectionAvailable] = useState(true);
  const [collectionConnectionError, setCollectionConnectionError] = useState('');
  const collectionCheckTimerRef = useRef(null);
  const collectionCheckSeqRef = useRef(0);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testMessages, setTestMessages] = useState([]);
  const [testInputValue, setTestInputValue] = useState('');
  const [testingInput, setTestingInput] = useState(false);
  const [groups, setGroups] = useState([]);
  // REMOVED: chunkingStrategy state - strategy will be selected per-document
  const [modelCatalog, setModelCatalog] = useState(null);
  const [modelCatalogLoading, setModelCatalogLoading] = useState(false);
  const [selectedLlmProvider, setSelectedLlmProvider] = useState('openai');
  const [s3Dirty, setS3Dirty] = useState(false);
  const [s3Verified, setS3Verified] = useState(false);
  const [s3Verifying, setS3Verifying] = useState(false);
  const [s3VerifyError, setS3VerifyError] = useState('');
  const [knowledgeProvider, setKnowledgeProvider] = useState(KNOWLEDGE_PROVIDER.S3);
  const [confluenceDirty, setConfluenceDirty] = useState(false);
  const [confluenceVerified, setConfluenceVerified] = useState(false);
  const [confluenceVerifying, setConfluenceVerifying] = useState(false);
  const [confluenceVerifyError, setConfluenceVerifyError] = useState('');
  const [confluenceCredentialId, setConfluenceCredentialId] = useState('');
  const [confluenceNormalized, setConfluenceNormalized] = useState(null);
  const [confluenceFolders, setConfluenceFolders] = useState([]);
  const [confluenceFoldersLoading, setConfluenceFoldersLoading] = useState(false);
  const [confluenceFoldersError, setConfluenceFoldersError] = useState('');
  const [selectedConfluenceFolderIds, setSelectedConfluenceFolderIds] = useState([]);
  const [githubDirty, setGithubDirty] = useState(false);
  const [githubVerified, setGithubVerified] = useState(false);
  const [githubVerifying, setGithubVerifying] = useState(false);
  const [githubVerifyError, setGithubVerifyError] = useState('');
  const [githubCredentialId, setGithubCredentialId] = useState('');
  const [githubNormalized, setGithubNormalized] = useState(null);
  const [jiraDirty, setJiraDirty] = useState(false);
  const [jiraVerified, setJiraVerified] = useState(false);
  const [jiraVerifying, setJiraVerifying] = useState(false);
  const [jiraVerifyError, setJiraVerifyError] = useState('');
  const [jiraCredentialId, setJiraCredentialId] = useState('');
  const [jiraNormalized, setJiraNormalized] = useState(null);
  const [azureWikiDirty, setAzureWikiDirty] = useState(false);
  const [azureWikiVerified, setAzureWikiVerified] = useState(false);
  const [azureWikiVerifying, setAzureWikiVerifying] = useState(false);
  const [azureWikiVerifyError, setAzureWikiVerifyError] = useState('');
  const [azureWikiCredentialId, setAzureWikiCredentialId] = useState('');
  const [azureWikiNormalized, setAzureWikiNormalized] = useState(null);

  useEffect(() => {
    if (knowledgeProvider !== KNOWLEDGE_PROVIDER.JIRA) return;
    const jiraCfg = form.getFieldValue(['config', 'jira']) || {};
    if (!String(jiraCfg.jira_base_url || '').trim()) {
      form.setFieldsValue({
        config: {
          ...(form.getFieldValue('config') || {}),
          jira: { ...jiraCfg, jira_base_url: DEFAULT_JIRA_BASE_URL },
        },
      });
    }
  }, [knowledgeProvider, form]);

  const guardsConfig = {
    NSFWText: {
      label: 'NSFW Text',
      description: 'Checks if LLM-generated text is not safe for work (NSFW), validating both sentence-by-sentence and the entire text.',
      parameters: [
        { name: 'threshold', label: 'Threshold', type: 'float', default: 0.8, description: 'Threshold for NSFW detection (0.0 to 1.0)' },
        { name: 'validation_method', label: 'Validation Method', type: 'select', default: 'sentence', options: ['sentence', 'full'], description: 'Method to validate: sentence-by-sentence or full text' },
        { name: 'on_fail', label: 'On Fail Policy', type: 'select', default: 'exception', options: ['reask', 'fix', 'filter', 'refrain', 'noop', 'exception', 'fix_reask'], description: 'Action to take when validation fails' }
      ]
    },
    RestrictToTopic: {
      label: 'Restrict To Topic',
      description: 'Restricts input text to valid topics and checks for invalid topics.',
      parameters: [
        { name: 'valid_topics', label: 'Valid Topics', type: 'tags', default: [], description: 'List of valid topics' },
        { name: 'invalid_topics', label: 'Invalid Topics', type: 'tags', default: [], description: 'List of invalid topics to reject' },
        { name: 'disable_llm', label: 'Disable LLM', type: 'boolean', default: false, description: 'Disable LLM-based topic detection' },
        { name: 'disable_classifier', label: 'Disable Classifier', type: 'boolean', default: true, description: 'Disable classifier-based topic detection' },
        { name: 'on_fail', label: 'On Fail Policy', type: 'select', default: 'exception', options: ['reask', 'fix', 'filter', 'refrain', 'noop', 'exception', 'fix_reask'], description: 'Action to take when validation fails' }
      ]
    },
    DetectPII: {
      label: 'Detect PII',
      description: 'Detects Personally Identifiable Information (PII) in text.',
      parameters: [
        { name: 'entities', label: 'PII Entities', type: 'multiselect', default: ['EMAIL_ADDRESS', 'PHONE_NUMBER'], options: ['EMAIL_ADDRESS', 'PHONE_NUMBER', 'CREDIT_CARD', 'SSN', 'IP_ADDRESS', 'PERSON', 'LOCATION', 'DATE_TIME'], description: 'List of PII entity types to detect' },
        { name: 'pii_score_threshold', label: 'Confidence Threshold', type: 'float', default: 0.7, description: 'Minimum confidence score (0.0-1.0) for PII detection. Higher = fewer false positives. Recommended: 0.7+' },
        { name: 'on_fail', label: 'On Fail Policy', type: 'select', default: 'noop', options: ['reask', 'fix', 'filter', 'refrain', 'noop', 'exception', 'fix_reask'], description: 'Action to take when validation fails' }
      ]
    }
  };

  const onFailDescriptions = {
    reask: 'Prompt user to provide corrected input',
    fix: 'Attempt to automatically fix the issue',
    filter: 'Remove problematic content from output',
    refrain: 'Skip generation and return empty response',
    noop: 'No action, allow content to pass through',
    exception: 'Raise an error and halt processing',
    fix_reask: 'Try to fix, if unsuccessful prompt user'
  };

  const formatS3Error = (detailRaw) => {
    let detail = '';
    if (detailRaw == null) {
      return 'Unable to verify S3 settings. Please check bucket name and region.';
    }
    if (typeof detailRaw === 'string') {
      detail = detailRaw;
    } else if (Array.isArray(detailRaw)) {
      detail = detailRaw
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item.msg === 'string') return item.msg;
          return '';
        })
        .filter(Boolean)
        .join(' ');
    } else if (typeof detailRaw === 'object' && typeof detailRaw.msg === 'string') {
      detail = detailRaw.msg;
    } else {
      try {
        detail = JSON.stringify(detailRaw);
      } catch {
        detail = String(detailRaw);
      }
    }
    if (!detail) return 'Unable to verify S3 settings. Please check bucket name and region.';
    if (detail.includes('Failed to access S3 bucket') && detail.includes('403')) {
      return 'Unable to access this S3 bucket. Please check the bucket name, region, and that your AWS credentials have at least read (HeadBucket/List) access.';
    }
    if (detail.includes('already registered to another project')) {
      return 'This S3 bucket is already linked to another project. Each project must use a unique bucket.';
    }
    const lower = detail.toLowerCase();
    const invalidBucket =
      lower.includes('invalid bucket name') ||
      lower.includes('bucket name must match') ||
      (lower.includes('parameter validation failed') && lower.includes('bucket')) ||
      (lower.includes('failed to access s3 bucket') && lower.includes('parameter validation failed'));
    if (invalidBucket) {
      return 'That name is not a valid S3 bucket. It cannot contain spaces or other disallowed characters. Use only letters, numbers, dots, hyphens, and underscores, between 3 and 63 characters, exactly as the bucket is named in the AWS S3 console.';
    }
    return detail;
  };

  const renderGuardConfig = (guardName, type) => {
    const guard = guardsConfig[guardName];
    if (!guard) return null;

    return (
      <div key={guardName} style={{ marginBottom: '16px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '4px', border: '1px solid rgba(148, 22, 127, 0.2)' }}>
        <h4 style={{ marginBottom: '4px', color: themeStyles.textPrimary }}>{guard.label}</h4>
        {guard.description && (
          <p style={{ marginBottom: '12px', fontSize: '13px', color: themeStyles.textSecondary, fontStyle: 'italic' }}>{guard.description}</p>
        )}
        {guard.parameters.map(param => {
          const fieldName = ['config', 'guardrails', `${type}_guards_config`, guardName, param.name];
          
          const label = param.label;

          let selectOrNumberInitial = param.default;
          if (param.name === 'on_fail') {
            if (guardName === 'DetectPII') {
              selectOrNumberInitial = type === 'pre_embedding' || type === 'context' ? 'fix' : 'noop';
            } else {
              selectOrNumberInitial = 'exception';
            }
          }
          
          if (param.type === 'float') {
            return (
              <Form.Item key={param.name} label={label} name={fieldName} initialValue={param.default}>
                <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            );
          } else if (param.type === 'select') {
            return (
              <Form.Item key={param.name} label={label} name={fieldName} initialValue={selectOrNumberInitial}>
                <Select optionLabelProp="label">
                  {param.options.map(opt => (
                    <Select.Option key={opt} value={opt} label={opt}>
                      <div>
                        <div>{opt}</div>
                        {onFailDescriptions[opt] && (
                          <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                            {onFailDescriptions[opt]}
                          </div>
                        )}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            );
          } else if (param.type === 'multiselect') {
            return (
              <Form.Item key={param.name} label={label} name={fieldName} initialValue={param.default}>
                <Select mode="multiple">
                  {param.options.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
                </Select>
              </Form.Item>
            );
          } else if (param.type === 'boolean') {
            return (
              <Form.Item key={param.name} label={label} name={fieldName} initialValue={param.default} valuePropName="checked">
                <Switch />
              </Form.Item>
            );
          } else if (param.type === 'tags') {
            return (
              <Form.Item key={param.name} label={label} name={fieldName} initialValue={param.default}>
                <Select mode="tags" placeholder="Type and press enter" />
              </Form.Item>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const steps = [
    { key: STEP_KEYS.PROJECT_DETAILS, title: 'Project Details' },
    { key: STEP_KEYS.KNOWLEDGE_SOURCE, title: 'Knowledge Source' },
    { key: STEP_KEYS.LLM, title: 'LLM' },
    { key: STEP_KEYS.EMBEDDING, title: 'Embedding' },
    { key: STEP_KEYS.VECTOR_STORE, title: 'Vector Store' },
    { key: STEP_KEYS.GRAPH_RAG, title: 'Graph RAG' },
    { key: STEP_KEYS.RETRIEVER, title: 'Retriever' },
    { key: STEP_KEYS.GUARDRAILS, title: 'Guardrails' },
    { key: STEP_KEYS.REVIEW, title: 'Review' },
  ];


  const currentStepKey = steps[currentStep]?.key;
  const getStepIndex = (key) => steps.findIndex((step) => step.key === key);

  const FieldLabel = ({ label, tooltip }) => <FieldTooltip label={label} tooltip={tooltip} />;

  const resetConfluenceFolderSelection = () => {
    setConfluenceFolders([]);
    setSelectedConfluenceFolderIds([]);
    setConfluenceFoldersError('');
    form.setFieldsValue({
      config: {
        ...(form.getFieldValue('config') || {}),
        confluence: {
          ...(form.getFieldValue(['config', 'confluence']) || {}),
          sync_scope: undefined,
        },
      },
    });
  };

  const resetConfluenceConnection = () => {
    resetConfluenceFolderSelection();
    form.setFieldsValue({
      config: {
        ...(form.getFieldValue('config') || {}),
        confluence: {
          ...(form.getFieldValue(['config', 'confluence']) || {}),
          space_key: undefined,
          space_id: undefined,
          space_name: undefined,
          sync_scope: undefined,
        },
      },
    });
  };

  const applyConfluenceFolderSelection = (folderIds, folders = confluenceFolders) => {
      const labels = {};
      folders.forEach((folder) => {
        if (folderIds.includes(folder.id)) {
          labels[folder.id] = folder.title;
        }
      });
      const rootTypes = {};
      folders.forEach((folder) => {
        if (folderIds.includes(folder.id)) {
          rootTypes[folder.id] = folder.type || 'page';
        }
      });
      const syncScope = {
        type: 'subtrees',
        parent_page_ids: folderIds,
        folder_labels: labels,
        root_types: rootTypes,
        include_attachments: false,
      };
    setSelectedConfluenceFolderIds(folderIds);
    form.setFieldsValue({
      config: {
        ...(form.getFieldValue('config') || {}),
        confluence: {
          ...(form.getFieldValue(['config', 'confluence']) || {}),
          sync_scope: syncScope,
        },
      },
    });
  };

  const loadConfluenceFolders = async (cf, normalized) => {
    if (!token) return;
    setConfluenceFoldersLoading(true);
    setConfluenceFoldersError('');
    try {
      const res = await API.listConfluenceFolders(token, {
        base_url: cf.base_url,
        space_key: cf.space_key,
        email: cf.email,
        api_token: cf.api_token,
        space_id: normalized?.space_id,
      });
      const folders = res.data?.folders || [];
      setConfluenceFolders(folders);
      if (folders.length === 0) {
        setConfluenceFoldersError('No folders or top-level pages found in this space.');
        setSelectedConfluenceFolderIds([]);
      }
    } catch (error) {
      const rawDetail = error?.response?.data?.detail ?? error?.message;
      setConfluenceFoldersError(String(rawDetail || 'Failed to load Confluence folders.'));
      setConfluenceFolders([]);
      setSelectedConfluenceFolderIds([]);
    } finally {
      setConfluenceFoldersLoading(false);
    }
  };

  const stepCompletionContext = useMemo(() => ({
    knowledgeProvider,
    s3Verified,
    confluenceVerified,
    githubVerified,
    jiraVerified,
    azureWikiVerified,
    graphragEnabled,
    showCollectionStatus,
    collectionAvailable,
    collectionConnectionError,
  }), [
    knowledgeProvider,
    s3Verified,
    confluenceVerified,
    githubVerified,
    jiraVerified,
    azureWikiVerified,
    graphragEnabled,
    showCollectionStatus,
    collectionAvailable,
    collectionConnectionError,
  ]);

  const isStepComplete = useCallback((stepIndex) => {
    const stepKey = steps[stepIndex]?.key;
    if (!stepKey) return false;
    const values = watchedFormValues ?? form.getFieldsValue(true);
    return isStepCompleteByKey(stepKey, values, stepCompletionContext);
  }, [watchedFormValues, form, stepCompletionContext, steps]);

  const currentStepComplete = useMemo(
    () => isStepComplete(currentStep),
    [isStepComplete, currentStep],
  );

  const canNavigateToStep = useCallback((targetIndex) => {
    if (targetIndex <= currentStep) return true;
    for (let i = 0; i < targetIndex; i += 1) {
      if (!completedSteps.has(i)) return false;
    }
    return true;
  }, [currentStep, completedSteps]);

  const handleStepChange = useCallback((targetStep) => {
    if (!canNavigateToStep(targetStep)) return;
    setCurrentStep(targetStep);
  }, [canNavigateToStep]);

  const stepItems = steps.map((step, index) => ({
    title: step.title,
    status: index === currentStep ? 'process' : completedSteps.has(index) ? 'finish' : 'wait',
    disabled: !canNavigateToStep(index),
  }));

  const checkCollectionAvailability = async (name, vectorStoreType) => {
    if (!name.trim()) {
      setShowCollectionStatus(false);
      setCollectionConnectionError('');
      return;
    }

    const username = localStorage.getItem('username') || '';
    const formattedCollectionName = formatCollectionNameWithUser(name, username);
    const requestSeq = ++collectionCheckSeqRef.current;

    setCheckingCollection(true);
    setCollectionConnectionError('');
    try {
      const response = await API.checkCollectionExists(formattedCollectionName, vectorStoreType, token);
      if (requestSeq !== collectionCheckSeqRef.current) {
        return;
      }

      const result = interpretCollectionCheckResponse(response.data, vectorStoreType);
      applyCollectionCheckResult(result, {
        setCollectionAvailable,
        setCollectionConnectionError,
        setShowCollectionStatus,
      });

      if (result.kind === 'exists') {
        message.warning(
          `Collection "${name}" already exists in ${vectorStoreType}. Please choose a different name.`,
        );
      } else if (result.kind === 'connection_error') {
        message.error(result.message);
      }
    } catch (error) {
      if (requestSeq !== collectionCheckSeqRef.current) {
        return;
      }
      console.error('Error checking collection:', error);
      const result = interpretCollectionCheckResponse(error.response?.data, vectorStoreType);
      applyCollectionCheckResult(result, {
        setCollectionAvailable,
        setCollectionConnectionError,
        setShowCollectionStatus,
      });
      if (result.kind === 'connection_error') {
        message.error(result.message);
      }
    } finally {
      if (requestSeq === collectionCheckSeqRef.current) {
        setCheckingCollection(false);
      }
    }
  };

  const scheduleCollectionAvailabilityCheck = (name, vectorStoreType) => {
    if (collectionCheckTimerRef.current) {
      clearTimeout(collectionCheckTimerRef.current);
    }
    setShowCollectionStatus(false);
    setCollectionConnectionError('');
    if (!name || !String(name).trim()) {
      return;
    }
    collectionCheckTimerRef.current = setTimeout(() => {
      checkCollectionAvailability(name, vectorStoreType);
    }, 450);
  };

  useEffect(() => () => {
    if (collectionCheckTimerRef.current) {
      clearTimeout(collectionCheckTimerRef.current);
    }
  }, []);

  const markCurrentStepIfComplete = () => {
    if (isStepComplete(currentStep)) {
      setCompletedSteps((prev) => new Set([...prev, currentStep]));
    }
  };

  const validateCurrentStep = async () => {
    const fieldNames = getStepValidateFieldNames(
      currentStepKey,
      knowledgeProvider,
      graphragEnabled,
    );
    if (fieldNames.length > 0) {
      await form.validateFields(fieldNames);
    }
    if (currentStepKey === STEP_KEYS.KNOWLEDGE_SOURCE) {
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.S3 && !s3Verified) {
        message.warning('Please verify the Knowledge Source configuration before proceeding.');
        throw new Error('knowledge_source_not_verified');
      }
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE && !confluenceVerified) {
        message.warning('Please verify Confluence configuration before proceeding.');
        throw new Error('knowledge_source_not_verified');
      }
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB && !githubVerified) {
        message.warning('Please connect GitHub before proceeding.');
        throw new Error('knowledge_source_not_verified');
      }
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA && !jiraVerified) {
        message.warning('Please verify Jira configuration before proceeding.');
        throw new Error('knowledge_source_not_verified');
      }
      if (knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI && !azureWikiVerified) {
        message.warning('Please connect Azure Wiki before proceeding.');
        throw new Error('knowledge_source_not_verified');
      }
    }
    if (currentStepKey === STEP_KEYS.VECTOR_STORE) {
      const coll = String(form.getFieldValue(['config', 'vector_store', 'collection_name']) ?? '').trim();
      if (coll && showCollectionStatus && (!collectionAvailable || collectionConnectionError)) {
        message.warning('Please choose an available collection name before proceeding.');
        throw new Error('collection_unavailable');
      }
    }
  };

  const next = async () => {
    try {
      await validateCurrentStep();
    } catch (error) {
      if (error?.errorFields) return;
      return;
    }
    markCurrentStepIfComplete();
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const activeCollectionName = String(collectionNameField ?? collectionName ?? '').trim();
  const vectorStoreNextBlocked =
    currentStepKey === STEP_KEYS.VECTOR_STORE
    && activeCollectionName !== ''
    && (
      checkingCollection
      || (showCollectionStatus && (!collectionAvailable || Boolean(collectionConnectionError)))
    );

  const nextDisabled =
    !currentStepComplete
    || vectorStoreNextBlocked
    || s3Verifying
    || confluenceVerifying
    || confluenceFoldersLoading
    || githubVerifying
    || jiraVerifying
    || azureWikiVerifying;

  const renderStepHeading = (title, withDivider = false) => (
    <div style={{ marginBottom: '24px' }}>
      <h2
        style={{
          margin: 0,
          fontSize: '24px',
          fontWeight: 600,
          color: themeStyles.textPrimary,
          textShadow: withDivider ? 'none' : undefined,
        }}
      >
        {title}
      </h2>
      {withDivider ? (
        <div
          style={{
            height: '1px',
            background: 'linear-gradient(90deg, rgba(148, 22, 127, 0.6) 0%, rgba(21, 60, 180, 0.6) 100%)',
            marginTop: '12px',
          }}
        />
      ) : null}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStepKey) {
      case STEP_KEYS.PROJECT_DETAILS:
        return (
          <>
            {renderStepHeading('Project Details')}
            <Form.Item
              label="Group"
              name="group_id"
              rules={[{ required: true, message: 'Please select a group' }]}
            >
              <Select
                placeholder="Select group"
                options={groups.map((g) => ({ label: g.name, value: g._id }))}
                // For group admins who are pre-assigned to a single group, selection is unnecessary.
                disabled={groups.length === 1}
              />
            </Form.Item>
            <Form.Item label="Project Name" name="name" rules={[{ required: true, message: 'Please enter project name' }]}>
              <Input placeholder="Enter project name" />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              rules={PROJECT_DESCRIPTION_FORM_RULES}
            >
              <TextArea
                rows={4}
                placeholder="Describe the domain, audience, and scope for this assistant"
                maxLength={500}
                showCount
              />
            </Form.Item>
          
          </>
        );
      case STEP_KEYS.EMBEDDING: {
        const embeddingTileOptions = [
          {
            value: 'openai',
            title: 'OpenAI',
            description: 'High-quality embeddings from OpenAI (uses server-side OPENAI_API_KEY)',
            icon: <ProviderLogo id="openai" />,
          },
          {
            value: 'bge',
            title: 'HuggingFace BGE',
            description: 'Open-source embeddings via HuggingFace (no API key required here)',
            icon: <ProviderLogo id="huggingface" />,
          },
        ];
        return (
          <>
            {renderStepHeading('Embedding')}
            <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '8px', borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                Select the embedding model to convert text into vector representations. These vectors enable semantic search and similarity matching.
              </p>
            </div>
            <Form.Item
              label="Embedding Model Provider"
              name={['config', 'embedding', 'model']}
              initialValue="openai"
              rules={[{ required: true, message: 'Please select an embedding provider' }]}
            >
              <FormTilePicker
                options={embeddingTileOptions}
                themeStyles={themeStyles}
                colors={colors}
                afterChange={(next) => {
                  const nextModelName =
                    next === 'bge' ? 'BAAI/bge-base-en-v1.5' : 'text-embedding-3-small';
                  form.setFieldsValue({
                    config: {
                      ...(form.getFieldValue('config') || {}),
                      embedding: {
                        ...(form.getFieldValue(['config', 'embedding']) || {}),
                        model_name: nextModelName,
                      },
                    },
                  });
                }}
              />
            </Form.Item>
            <Form.Item
              label="Model Name"
              name={['config', 'embedding', 'model_name']}
              initialValue="text-embedding-3-small"
              rules={[{ required: true, message: 'Please select a model name' }]}
            >
              <Select optionLabelProp="label">
                {selectedEmbeddingProvider === 'bge' ? (
                  <>
                    <Select.Option value="BAAI/bge-base-en-v1.5" label="BAAI/bge-base-en-v1.5">
                      <div>
                        <div>BAAI/bge-base-en-v1.5</div>
                        <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                          HuggingFace BGE base model (balanced speed/quality)
                        </div>
                      </div>
                    </Select.Option>
                    <Select.Option value="BAAI/bge-large-en-v1.5" label="BAAI/bge-large-en-v1.5">
                      <div>
                        <div>BAAI/bge-large-en-v1.5</div>
                        <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                          Higher-quality BGE model with higher compute usage
                        </div>
                      </div>
                    </Select.Option>
                    <Select.Option value="BAAI/bge-m3" label="BAAI/bge-m3">
                      <div>
                        <div>BAAI/bge-m3</div>
                        <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                          Multilingual BGE model for broad language coverage
                        </div>
                      </div>
                    </Select.Option>
                  </>
                ) : (
                  <>
                    <Select.Option value="text-embedding-3-small" label="text-embedding-3-small">
                      <div>
                        <div>text-embedding-3-small</div>
                        <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                          OpenAI recommended default for balanced quality and cost
                        </div>
                      </div>
                    </Select.Option>
                    <Select.Option value="text-embedding-3-large" label="text-embedding-3-large">
                      <div>
                        <div>text-embedding-3-large</div>
                        <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                          Higher-quality OpenAI embeddings with larger vector size
                        </div>
                      </div>
                    </Select.Option>
                    <Select.Option value="text-embedding-ada-002" label="text-embedding-ada-002">
                      <div>
                        <div>text-embedding-ada-002</div>
                        <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                          Legacy OpenAI embedding model (kept for compatibility)
                        </div>
                      </div>
                    </Select.Option>
                  </>
                )}
              </Select>
            </Form.Item>
            {/* API key taken from backend environment; no user input required */}
          </>
        );
      }
      case STEP_KEYS.VECTOR_STORE: {
        const vectorTileOptions = [
          {
            value: 'milvus',
            title: 'Milvus',
            description: 'Open-source vector database, self-hosted and scalable',
            icon: <ProviderLogo id="milvus" />,
          },
          {
            value: 'pinecone',
            title: 'Pinecone',
            description: 'Managed cloud vector database (not available yet).',
            icon: <ProviderLogo id="pinecone" />,
            disabled: true,
          },
        ];
        return (
          <>
            {renderStepHeading('Vector Store')}
            <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '8px', borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                Choose where to store your vector embeddings. The vector store enables fast similarity search across your document collection.
              </p>
            </div>
            <Form.Item
              label="Vector Store Type"
              name={['config', 'vector_store', 'type']}
              initialValue="milvus"
              rules={[{ required: true, message: 'Please select a vector store type' }]}
            >
              <FormTilePicker
                options={vectorTileOptions}
                themeStyles={themeStyles}
                colors={colors}
                afterChange={(storeType) => {
                  setShowCollectionStatus(false);
                  setCollectionAvailable(true);
                  setCollectionConnectionError('');
                  if (collectionName.trim()) {
                    scheduleCollectionAvailabilityCheck(collectionName, storeType);
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              label="Collection Name"
              name={['config', 'vector_store', 'collection_name']}
              rules={[{ required: true, whitespace: true, message: 'Please enter the collection name' }]}
            >
              <Input placeholder="Enter collection name" onChange={(e) => {
                const value = e.target.value;
                setCollectionName(value);
                const vectorStoreType = form.getFieldValue(['config', 'vector_store', 'type']) || 'milvus';
                scheduleCollectionAvailabilityCheck(value, vectorStoreType);
              }} />
            </Form.Item>
            {checkingCollection && collectionName.trim() && (
              <div style={{ marginTop: '-16px', marginBottom: '16px', padding: '8px 12px', background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)', borderRadius: '4px' }}>
                <span style={{ color: themeStyles.textSecondary, fontSize: '13px' }}>Checking availability...</span>
              </div>
            )}
            {showCollectionStatus && collectionName.trim() && !checkingCollection && (
              <div style={{ 
                marginTop: '-16px', 
                marginBottom: '16px', 
                padding: '8px 12px', 
                background: collectionConnectionError
                  ? 'rgba(250, 173, 20, 0.1)'
                  : collectionAvailable
                    ? 'rgba(57, 255, 20, 0.1)' 
                    : 'rgba(255, 77, 79, 0.1)', 
                border: `1px solid ${
                  collectionConnectionError
                    ? 'rgba(250, 173, 20, 0.35)'
                    : collectionAvailable
                      ? 'rgba(57, 255, 20, 0.3)'
                      : 'rgba(255, 77, 79, 0.3)'
                }`, 
                borderRadius: '4px' 
              }}>
                <span style={{ 
                  color: collectionConnectionError ? '#faad14' : collectionAvailable ? '#39ff14' : '#ff4d4f', 
                  fontSize: '13px' 
                }}>
                  {collectionConnectionError
                    ? `⚠ ${collectionConnectionError}`
                    : collectionAvailable
                      ? '✓ Collection name is available'
                      : '✗ Collection name already exists'}
                </span>
              </div>
            )}
          </>
        );
      }
      case STEP_KEYS.KNOWLEDGE_SOURCE:
        return (
          <>
            {renderStepHeading('Knowledge Source')}
            <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '8px', borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                Configure the Knowledge Source used to store raw documents for this project. Each source can be assigned to only one project.
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'stretch',
                gap: 12,
                marginBottom: 24,
              }}
            >
              {[
                {
                  key: KNOWLEDGE_PROVIDER.S3,
                  title: 'Amazon S3',
                  icon: <ProviderLogo id="s3" />,
                },
                {
                  key: KNOWLEDGE_PROVIDER.GITHUB,
                  title: 'GitHub',
                  icon: <GithubOutlined style={{ fontSize: 28 }} />,
                },
                {
                  key: KNOWLEDGE_PROVIDER.CONFLUENCE,
                  title: 'Confluence',
                  icon: <ProviderLogo id="confluence" />,
                },
                {
                  key: KNOWLEDGE_PROVIDER.JIRA,
                  title: 'Jira',
                  icon: <ProviderLogo id="jira" />,
                },
                {
                  key: KNOWLEDGE_PROVIDER.AZURE_WIKI,
                  title: 'Azure Wiki',
                  icon: <ProviderLogo id="azure_wiki" />,
                },
              ].map((opt) => {
                const active = knowledgeProvider === opt.key;
                return (
                  <div
                    key={opt.key}
                    style={{ flex: '1 1 0', minWidth: 140, display: 'flex' }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setKnowledgeProvider(opt.key);
                        if (opt.key !== KNOWLEDGE_PROVIDER.S3) {
                          setS3Verified(false);
                          setS3Dirty(false);
                        }
                        if (opt.key !== KNOWLEDGE_PROVIDER.CONFLUENCE) {
                          setConfluenceVerified(false);
                          setConfluenceDirty(false);
                        }
                        if (opt.key !== KNOWLEDGE_PROVIDER.GITHUB) {
                          setGithubVerified(false);
                          setGithubDirty(false);
                        }
                        if (opt.key !== KNOWLEDGE_PROVIDER.JIRA) {
                          setJiraVerified(false);
                          setJiraDirty(false);
                        }
                        if (opt.key !== KNOWLEDGE_PROVIDER.AZURE_WIKI) {
                          setAzureWikiVerified(false);
                          setAzureWikiDirty(false);
                        }
                      }}
                      style={{
                        width: '100%',
                        height: 100,
                        boxSizing: 'border-box',
                        padding: '14px 16px',
                        borderRadius: 10,
                        cursor: 'pointer',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: active
                          ? `2px solid ${themeStyles.borderAccent}`
                          : `1px solid ${themeStyles.borderColor}`,
                        background: active ? themeStyles.elevatedBg : themeStyles.inputBg,
                        color: themeStyles.textPrimary,
                        boxShadow: active ? themeStyles.shadowSm : 'none',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                      }}
                    >
                      <div style={{ color: active ? colors.primary : themeStyles.textSecondary, marginBottom: 8 }}>{opt.icon}</div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{opt.title}</div>
                    </button>
                  </div>
                );
              })}
            </div>

            {knowledgeProvider === KNOWLEDGE_PROVIDER.S3 ? (
              <>
            <Form.Item
              label="S3 Bucket Name"
              name={['config', 's3', 'bucket']}
              rules={[{ required: true, message: 'Please enter the S3 Bucket name' }]}
            >
              <Input
                placeholder="my-project-bucket"
                onChange={() => {
                  setS3Dirty(true);
                  setS3Verified(false);
                  setS3VerifyError('');
                }}
              />
            </Form.Item>
            <Form.Item
              label="S3 Region"
              name={['config', 's3', 'region']}
              rules={[{ required: true, message: 'Please select the S3 region' }]}
            >
              <Select
                showSearch
                placeholder="Select region"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label || '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={() => {
                  setS3Dirty(true);
                  setS3Verified(false);
                  setS3VerifyError('');
                }}
                options={S3_REGION_OPTIONS.map((region) => ({
                  value: region,
                  label: region,
                }))}
              />
            </Form.Item>
            {s3VerifyError && (
              <div
                style={{
                  marginTop: '-8px',
                  marginBottom: '12px',
                  color: '#ff4d4f',
                  fontSize: '13px',
                }}
              >
                {s3VerifyError}
              </div>
            )}
            <Button
              type={s3Verified ? "primary" : "default"}
              onClick={async () => {
                try {
                  const values = await form.validateFields([
                    ['config', 's3', 'bucket'],
                    ['config', 's3', 'region'],
                  ]);
                  const bucket = values?.config?.s3?.bucket;
                  const region = values?.config?.s3?.region;
                  if (!bucket || !region) {
                    message.error('Please fill S3 bucket name and region before verifying.');
                    return;
                  }
                  if (!token) {
                    message.error('You must be logged in to verify S3 configuration.');
                    return;
                  }
                  setS3Verifying(true);
                  setS3VerifyError('');
                  await API.verifyS3Config(token, { bucket, region });
                  message.success(formatKnowledgeSourceVerified(bucket, { forCreate: true }));
                  setS3Dirty(false);
                  setS3Verified(true);
                } catch (error) {
                  console.error('Error verifying S3 configuration (create):', error);
                  const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.message;
                  if (rawDetail) {
                    const friendly = humanizeApiError(rawDetail, {
                      context: ERROR_CONTEXT.KNOWLEDGE_SOURCE_VERIFY,
                    });
                    setS3VerifyError(friendly);
                    message.error(friendly);
                  } else if (error?.errorFields) {
                    // Validation error from form.validateFields
                    message.error('Please fix the highlighted S3 fields.');
                  } else {
                    setS3VerifyError('Failed to verify S3 configuration. Please check bucket and region.');
                    message.error('Failed to verify S3 configuration. Please check bucket and region.');
                  }
                } finally {
                  setS3Verifying(false);
                }
              }}
              loading={s3Verifying}
              disabled={s3Verifying}
              style={{
                marginTop: 8,
                background: s3Verified
                  ? 'linear-gradient(135deg, #52c41a 0%, #237804 100%)'
                  : undefined,
                borderColor: s3Verified ? '#52c41a' : undefined,
                color: s3Verified ? themeStyles.textInverse : undefined,
              }}
            >
              {s3Verified ? 'Verified' : 'Verify'}
            </Button>
              </>
            ) : null}

            {knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB ? (
              <>
                <div style={{ marginBottom: 16, padding: 12, background: 'rgba(148, 22, 127, 0.05)', borderRadius: 8, borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
                  <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: 13, lineHeight: 1.55 }}>
                    Pipeline artifacts are stored in the shared bucket <code>hrms-aisolutions</code> (us-east-2).
                    Files are synced from your GitHub repository on a schedule or when you click Sync.
                  </p>
                </div>
                <Form.Item
                  label="GitHub base URL"
                  name={['config', 'github', 'github_base_url']}
                  rules={[{ required: true, message: 'GitHub base URL is required' }]}
                >
                  <Input
                    placeholder="https://github.com/sai25113"
                    onChange={() => {
                      setGithubDirty(true);
                      setGithubVerified(false);
                      setGithubVerifyError('');
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Personal access token (PAT)"
                  name={['config', 'github', 'pat']}
                  rules={[{ required: true, message: 'PAT is required' }]}
                >
                  <Input.Password
                    placeholder="github_pat_... or ghp_..."
                    onChange={() => {
                      setGithubDirty(true);
                      setGithubVerified(false);
                      setGithubVerifyError('');
                    }}
                  />
                </Form.Item>
                {githubVerifyError ? (
                  <div style={{ color: '#ff7875', marginBottom: 12, fontSize: 13 }}>{githubVerifyError}</div>
                ) : null}
                <Button
                  type={githubVerified ? 'primary' : 'default'}
                  onClick={async () => {
                    try {
                      await form.validateFields([
                        ['config', 'github', 'github_base_url'],
                        ['config', 'github', 'pat'],
                      ]);
                      const gf = form.getFieldValue(['config', 'github']) || {};
                      setGithubVerifying(true);
                      setGithubVerifyError('');
                      const res = await API.connectGithub(token, {
                        github_base_url: gf.github_base_url || '',
                        pat: gf.pat,
                      });
                      const data = res?.data || {};
                      setGithubCredentialId(data.credential_id || '');
                      setGithubNormalized(data.normalized || {});
                      setGithubVerified(true);
                      setGithubDirty(false);
                      form.setFieldsValue({
                        config: {
                          ...(form.getFieldValue('config') || {}),
                          github: {
                            ...(form.getFieldValue(['config', 'github']) || {}),
                            github_base_url: data.normalized?.github_base_url || gf.github_base_url,
                          },
                        },
                      });
                      message.success('GitHub account connected. Configure repositories/branches in Source Settings after project creation.');
                    } catch (e) {
                      setGithubVerified(false);
                      setGithubVerifyError(e?.response?.data?.detail || e?.message || 'Connection failed');
                    } finally {
                      setGithubVerifying(false);
                    }
                  }}
                  loading={githubVerifying}
                  disabled={githubVerifying}
                  style={{
                    marginBottom: 16,
                    background: githubVerified ? '#52c41a' : undefined,
                    borderColor: githubVerified ? '#52c41a' : undefined,
                    color: githubVerified ? themeStyles.textInverse : undefined,
                  }}
                >
                  {githubVerified ? 'Connected' : 'Connect'}
                </Button>
              </>
            ) : null}

            {knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE ? (
              <>
                <div style={{ marginBottom: 16, padding: 12, background: 'rgba(148, 22, 127, 0.05)', borderRadius: 8, borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
                  <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: 13, lineHeight: 1.55 }}>
                    Pipeline artifacts (Data Processed / Data Approved) are stored in the shared bucket{' '}
                    <code>hrms-aisolutions</code> (us-east-2) under a folder named from your project.
                  </p>
                </div>
                <Form.Item
                  label="Wiki Base URL"
                  name={['config', 'confluence', 'base_url']}
                  rules={[{ required: true, message: 'Please enter the Confluence wiki base URL' }]}
                >
                  <Input
                    placeholder="https://your-domain.atlassian.net/wiki"
                    onChange={() => {
                      setConfluenceDirty(true);
                      setConfluenceVerified(false);
                      setConfluenceVerifyError('');
                      resetConfluenceConnection();
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Atlassian Account Email"
                  name={['config', 'confluence', 'email']}
                  rules={[{ required: true, message: 'Please enter the account email' }, { type: 'email', message: 'Invalid email' }]}
                >
                  <Input
                    placeholder="bot@company.com"
                    onChange={() => {
                      setConfluenceDirty(true);
                      setConfluenceVerified(false);
                      setConfluenceVerifyError('');
                      resetConfluenceConnection();
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="API Token"
                  name={['config', 'confluence', 'api_token']}
                  rules={[{ required: true, message: 'Please enter the API token' }]}
                >
                  <Input.Password
                    placeholder="ATATT..."
                    onChange={() => {
                      setConfluenceDirty(true);
                      setConfluenceVerified(false);
                      setConfluenceVerifyError('');
                      resetConfluenceConnection();
                    }}
                  />
                </Form.Item>
                {confluenceVerifyError ? (
                  <div style={{ marginTop: '-8px', marginBottom: '12px', color: '#ff4d4f', fontSize: '13px' }}>
                    {confluenceVerifyError}
                  </div>
                ) : null}
                <Button
                  type={confluenceVerified ? 'primary' : 'default'}
                  onClick={async () => {
                    try {
                      const values = await form.validateFields([
                        ['config', 'confluence', 'base_url'],
                        ['config', 'confluence', 'email'],
                        ['config', 'confluence', 'api_token'],
                      ]);
                      const cf = values?.config?.confluence || {};
                      if (!token) {
                        message.error('You must be logged in to verify Confluence configuration.');
                        return;
                      }
                      setConfluenceVerifying(true);
                      setConfluenceVerifyError('');
                      resetConfluenceConnection();
                      const res = await API.listConfluenceSpaces(token, {
                        base_url: cf.base_url,
                        email: cf.email,
                        api_token: cf.api_token,
                      });
                      const data = res.data || {};
                      const normalized = data.normalized || {};
                      setConfluenceCredentialId(data.credential_id || '');
                      setConfluenceNormalized(normalized);
                      form.setFieldsValue({
                        config: {
                          ...(form.getFieldValue('config') || {}),
                          confluence: {
                            ...(form.getFieldValue(['config', 'confluence']) || {}),
                            ...normalized,
                            credential_id: data.credential_id,
                            space_key: undefined,
                            space_id: undefined,
                            space_name: undefined,
                            sync_scope: undefined,
                          },
                        },
                      });
                      message.success('Confluence account connected. Choose space and folders in Source Settings after the project is created.');
                      setConfluenceDirty(false);
                      setConfluenceVerified(true);
                    } catch (error) {
                      const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.message;
                      const friendly = humanizeConfluenceError(rawDetail);
                      setConfluenceVerifyError(friendly);
                      message.error(friendly);
                    } finally {
                      setConfluenceVerifying(false);
                    }
                  }}
                  loading={confluenceVerifying}
                  disabled={confluenceVerifying}
                  style={{
                    marginTop: 8,
                    background: confluenceVerified
                      ? 'linear-gradient(135deg, #52c41a 0%, #237804 100%)'
                      : undefined,
                    borderColor: confluenceVerified ? '#52c41a' : undefined,
                    color: confluenceVerified ? themeStyles.textInverse : undefined,
                  }}
                >
                  {confluenceVerified ? 'Connected' : 'Connect'}
                </Button>
                {confluenceVerified ? (
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginTop: 16 }}
                    message="Connected — not saved yet"
                    description="After the project is created, open RAG Pipeline → Source Settings to choose a Confluence space and select folders or pages to sync."
                  />
                ) : null}
              </>
            ) : null}

            {knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA ? (
              <>
                <div style={{ marginBottom: 16, padding: 12, background: 'rgba(148, 22, 127, 0.05)', borderRadius: 8, borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
                  <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: 13, lineHeight: 1.55 }}>
                    Pipeline artifacts (Data Processed / Data Approved) are stored in the shared bucket{' '}
                    <code>hrms-aisolutions</code> (us-east-2) under a folder named from your project.
                    Connect Jira here, then choose project keys and issue scope in Source Settings after project creation.
                  </p>
                </div>
                <Form.Item label={<FieldLabel label="Jira Base URL" tooltip="Atlassian Cloud site URL (e.g. https://your-domain.atlassian.net)." />}>
                  <Form.Item
                    name={['config', 'jira', 'jira_base_url']}
                    rules={[{ required: true, message: 'Please enter the Jira base URL' }]}
                    noStyle
                  >
                    <Input
                      placeholder={DEFAULT_JIRA_BASE_URL}
                      onChange={() => {
                        setJiraDirty(true);
                        setJiraVerified(false);
                        setJiraVerifyError('');
                      }}
                    />
                  </Form.Item>
                </Form.Item>
                <Form.Item
                  label={<FieldLabel label="Atlassian Account Email" tooltip="Email for the Jira API token (Cloud)." />}
                  name={['config', 'jira', 'jira_user_email']}
                  rules={[
                    { required: true, message: 'Please enter the account email' },
                    { type: 'email', message: 'Invalid email' },
                  ]}
                >
                  <Input
                    placeholder="you@company.com"
                    onChange={() => {
                      setJiraDirty(true);
                      setJiraVerified(false);
                      setJiraVerifyError('');
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label={<FieldLabel label="API Token" tooltip="Create at id.atlassian.com → Security → API tokens." />}
                  name={['config', 'jira', 'jira_api_token']}
                  rules={[{ required: true, message: 'Please enter the API token' }]}
                >
                  <Input.Password
                    placeholder="ATATT..."
                    onChange={() => {
                      setJiraDirty(true);
                      setJiraVerified(false);
                      setJiraVerifyError('');
                    }}
                  />
                </Form.Item>
                {jiraVerifyError ? (
                  <div style={{ marginTop: '-8px', marginBottom: '12px', color: '#ff7875', fontSize: '13px' }}>
                    {jiraVerifyError}
                  </div>
                ) : null}
                <Button
                  type={jiraVerified ? 'primary' : 'default'}
                  onClick={async () => {
                    try {
                      const fields = [
                        ['config', 'jira', 'jira_base_url'],
                        ['config', 'jira', 'jira_user_email'],
                        ['config', 'jira', 'jira_api_token'],
                      ];
                      const values = await form.validateFields(fields);
                      const jf = values?.config?.jira || {};
                      if (!token) {
                        message.error('You must be logged in to verify Jira configuration.');
                        return;
                      }
                      setJiraVerifying(true);
                      setJiraVerifyError('');
                      const payload = {
                        jira_base_url: jf.jira_base_url,
                        jira_user_email: jf.jira_user_email,
                        jira_api_token: jf.jira_api_token,
                      };
                      const res = await API.listJiraProjects(token, payload);
                      const data = res.data || {};
                      setJiraCredentialId(data.credential_id || '');
                      setJiraNormalized(data.normalized || null);
                      form.setFieldsValue({
                        config: {
                          ...(form.getFieldValue('config') || {}),
                          jira: {
                            ...(form.getFieldValue(['config', 'jira']) || {}),
                            ...data.normalized,
                            credential_id: data.credential_id,
                          },
                        },
                      });
                      message.success('Jira account connected. Configure project keys and scope in Source Settings after creation.');
                      setJiraDirty(false);
                      setJiraVerified(true);
                    } catch (error) {
                      const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.message;
                      const friendly = formatS3Error(rawDetail).replace(/S3/gi, 'Jira');
                      setJiraVerifyError(friendly);
                      message.error(friendly);
                    } finally {
                      setJiraVerifying(false);
                    }
                  }}
                  loading={jiraVerifying}
                  disabled={jiraVerifying}
                  style={{
                    marginTop: 8,
                    background: jiraVerified
                      ? 'linear-gradient(135deg, #52c41a 0%, #237804 100%)'
                      : undefined,
                    borderColor: jiraVerified ? '#52c41a' : undefined,
                    color: jiraVerified ? '#ffffff' : undefined,
                  }}
                >
                  {jiraVerified ? 'Connected' : 'Connect'}
                </Button>
              </>
            ) : null}

            {knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI ? (
              <>
                <div style={{ marginBottom: 16, padding: 12, background: 'rgba(148, 22, 127, 0.05)', borderRadius: 8, borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
                  <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: 13, lineHeight: 1.55 }}>
                    Pipeline artifacts (Data Processed / Data Approved) are stored in the shared bucket{' '}
                    <code>hrms-aisolutions</code> (us-east-2) under a folder named from your project.
                  </p>
                </div>
                <Form.Item
                  label="Organization URL"
                  name={['config', 'azure_wiki', 'organization_url']}
                  rules={[{ required: true, message: 'Please enter the Azure DevOps organization URL' }]}
                >
                  <Input
                    placeholder="https://dev.azure.com/your-organization"
                    onChange={() => {
                      setAzureWikiDirty(true);
                      setAzureWikiVerified(false);
                      setAzureWikiVerifyError('');
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Personal Access Token (PAT)"
                  name={['config', 'azure_wiki', 'pat']}
                  rules={[{ required: true, message: 'Please enter the personal access token' }]}
                >
                  <Input.Password
                    placeholder="Azure DevOps PAT"
                    onChange={() => {
                      setAzureWikiDirty(true);
                      setAzureWikiVerified(false);
                      setAzureWikiVerifyError('');
                    }}
                  />
                </Form.Item>
                {azureWikiVerifyError ? (
                  <div style={{ marginTop: '-8px', marginBottom: '12px', color: '#ff4d4f', fontSize: '13px' }}>
                    {azureWikiVerifyError}
                  </div>
                ) : null}
                <Button
                  type={azureWikiVerified ? 'primary' : 'default'}
                  onClick={async () => {
                    try {
                      const values = await form.validateFields([
                        ['config', 'azure_wiki', 'organization_url'],
                        ['config', 'azure_wiki', 'pat'],
                      ]);
                      const aw = values?.config?.azure_wiki || {};
                      if (!token) {
                        message.error('You must be logged in to connect Azure Wiki.');
                        return;
                      }
                      setAzureWikiVerifying(true);
                      setAzureWikiVerifyError('');
                      const res = await API.connectAzureWiki(token, {
                        organization_url: aw.organization_url,
                        pat: aw.pat,
                      });
                      const data = res.data || {};
                      const normalized = data.normalized || {};
                      setAzureWikiCredentialId(data.credential_id || '');
                      setAzureWikiNormalized(normalized);
                      form.setFieldsValue({
                        config: {
                          ...(form.getFieldValue('config') || {}),
                          azure_wiki: {
                            ...(form.getFieldValue(['config', 'azure_wiki']) || {}),
                            ...normalized,
                            credential_id: data.credential_id,
                          },
                        },
                      });
                      message.success('Azure Wiki account connected. Choose projects and wikis in Source Settings after the project is created.');
                      setAzureWikiDirty(false);
                      setAzureWikiVerified(true);
                    } catch (error) {
                      const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.message;
                      const friendly = rawDetail
                        ? humanizeApiError(rawDetail, { context: ERROR_CONTEXT.KNOWLEDGE_SOURCE_VERIFY })
                        : 'Failed to connect to Azure Wiki. Please check the organization URL and PAT.';
                      setAzureWikiVerifyError(friendly);
                      message.error(friendly);
                    } finally {
                      setAzureWikiVerifying(false);
                    }
                  }}
                  loading={azureWikiVerifying}
                  disabled={azureWikiVerifying}
                  style={{
                    marginTop: 8,
                    background: azureWikiVerified
                      ? 'linear-gradient(135deg, #52c41a 0%, #237804 100%)'
                      : undefined,
                    borderColor: azureWikiVerified ? '#52c41a' : undefined,
                    color: azureWikiVerified ? themeStyles.textInverse : undefined,
                  }}
                >
                  {azureWikiVerified ? 'Connected' : 'Connect'}
                </Button>
                {azureWikiVerified ? (
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginTop: 16 }}
                    message="Connected — not saved yet"
                    description="After the project is created, open RAG Pipeline → Source Settings to choose Azure DevOps projects, wikis, and pages to sync."
                  />
                ) : null}
              </>
            ) : null}
          </>
        );
      case STEP_KEYS.RETRIEVER:
        return (
          <>
            {renderStepHeading('Retriever')}
            <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '8px', borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                Configure how relevant documents are retrieved from the vector store. These settings control the number and type of results returned.
              </p>
            </div>
            <Form.Item
              label="Search Type"
              name={['config', 'retriever', 'search_type']}
              initialValue={DEFAULT_RETRIEVER_SEARCH_TYPE}
              rules={[{ required: true, message: 'Please select a search type' }]}
            >
              <RetrieverSearchTypeSelect
                hintStyle={{ fontSize: '12px', color: themeStyles.textSecondary, lineHeight: 1.45 }}
              />
            </Form.Item>
            <Form.Item label="Top K" name={['config', 'retriever', 'top_k']} initialValue={5}>
              <InputNumber min={1} max={20} style={{ width: '100%' }} />
            </Form.Item>
            {retrieverSearchType === 'similarity' && (
              <>
                <Form.Item
                  label={<FieldLabel label="Cosine Similarity Threshold" tooltip="Only keep chunks with similarity score greater than or equal to this value. Leave empty to disable threshold filtering." />}
                  name={['config', 'retriever', 'score_threshold']}
                >
                  <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} placeholder="e.g. 0.75" />
                </Form.Item>
                <Form.Item
                  label={<FieldLabel label="Use Cosine Similarity" tooltip="When enabled, threshold treats higher scores as better matches." />}
                  name={['config', 'retriever', 'cosine_similarity']}
                  initialValue={true}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </>
            )}
            {retrieverSearchType === 'mmr' && (
              <>
                <Form.Item
                  label={<FieldLabel label="MMR Fetch K" tooltip="Number of candidates fetched before MMR reranks." />}
                  name={['config', 'retriever', 'fetch_k']}
                  initialValue={20}
                >
                  <InputNumber min={1} max={200} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  label={<FieldLabel label="MMR Lambda" tooltip="0 = more diversity, 1 = more relevance." />}
                  name={['config', 'retriever', 'lambda_mult']}
                  initialValue={0.5}
                >
                  <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </>
            )}
            {retrieverSearchType === 'hybrid' && (
              <>
                <Form.Item
                  label={<FieldLabel label="BM25 K" tooltip="Number of lexical (exact-term) candidates from BM25." />}
                  name={['config', 'retriever', 'bm25_k']}
                  initialValue={6}
                >
                  <InputNumber min={1} max={200} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  label={<FieldLabel label="Vector K" tooltip="Number of semantic candidates from vector search." />}
                  name={['config', 'retriever', 'vector_k']}
                  initialValue={6}
                >
                  <InputNumber min={1} max={200} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  label={<FieldLabel label="Hybrid Alpha" tooltip="Semantic weight. 1.0 = all vector, 0.0 = all BM25." />}
                  name={['config', 'retriever', 'hybrid_alpha']}
                  initialValue={0.6}
                >
                  <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </>
            )}
          </>
        );
      case STEP_KEYS.LLM: {
        const llmProviders = mergeLlmProvidersForUi(modelCatalog?.providers);

        const llmIconById = {
          openai: <ProviderLogo id="openai" />,
          bedrock: <ProviderLogo id="bedrock" />,
          ollama: <ProviderLogo id="ollama" />,
          qwen: <ProviderLogo id="qwen" />,
          vllm: <ProviderLogo id="vllm" />,
          gemini: <ProviderLogo id="gemini" />,
          azure: <ProviderLogo id="azure" />,
        };

        const llmTileOptions = llmProviders.map((provider) => {
          const id = provider.id;
          let description = provider.description;
          if (id === 'openai' && !description) {
            description = 'GPT models for generating responses (uses server-side API key)';
          } else if (id === 'bedrock' && !description) {
            description = 'Amazon Bedrock hosted LLMs (keys managed on server)';
          } else if (id === 'ollama' && !description) {
            description = 'Local models on Ollama (Llama 3.2, Qwen 3.5 9B, etc.)';
          } else if (id === 'qwen' && !description) {
            description = 'Legacy Qwen provider — use Ollama instead';
          } else if (id === 'vllm' && !description) {
            description = 'High-throughput LLM serving via vLLM OpenAI-compatible API (server URL in deployment)';
          }
          return {
            value: id,
            title: provider.label,
            disabled: !!provider.disabled,
            description,
            icon: llmIconById[id] || <RobotOutlined style={{ fontSize: 28 }} />,
          };
        });

        return (
          <>
            {renderStepHeading('LLM')}
            <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '8px', borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                Select the language model that will generate responses based on retrieved context. Temperature controls response creativity.
              </p>
            </div>
            <Form.Item
              label="LLM Provider"
              name={['config', 'llm', 'provider']}
              initialValue={modelCatalog?.defaults?.llm_provider || 'openai'}
              rules={[{ required: true, message: 'Please select an LLM provider' }]}
            >
              <FormTilePicker
                options={llmTileOptions}
                columns={3}
                themeStyles={themeStyles}
                colors={colors}
                loading={modelCatalogLoading}
                afterChange={(providerId) => {
                  setSelectedLlmProvider(providerId);
                  const providerDefaultModel = modelCatalog?.providers
                    ?.find((p) => p.id === providerId)
                    ?.models?.[0]?.id;
                  if (providerDefaultModel) {
                    form.setFieldsValue({
                      config: {
                        ...(form.getFieldValue('config') || {}),
                        llm: {
                          ...(form.getFieldValue(['config', 'llm']) || {}),
                          model: providerDefaultModel,
                        },
                      },
                    });
                  }
                }}
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item
                label="Model"
                name={['config', 'llm', 'model']}
                initialValue={modelCatalog?.defaults?.llm_model || 'gpt-4o-mini'}
                style={{ flex: 1 }}
                rules={[{ required: true, message: 'Please select a model' }]}
              >
                {modelCatalog && modelCatalog.providers ? (
                  <Select
                    showSearch
                    placeholder="Select model"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {(modelCatalog.providers.find((p) => p.id === selectedLlmProvider)?.models ||
                      modelCatalog.providers.flatMap((p) => p.models)).map((model) => (
                      <Select.Option key={model.id} value={model.id}>
                        {model.label}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Input placeholder="Enter model name" />
                )}
              </Form.Item>
              <Form.Item label={<FieldLabel label="Temperature" tooltip="Controls creativity of LLM responses. 0 = factual/precise, 1 = most creative" />} name={['config', 'llm', 'temperature']} initialValue={0.6} style={{ flex: 1 }}>
                <Select 
                  optionLabelProp="label"
                  dropdownStyle={{ 
                    background: '#1f1f1f',
                    border: '1px solid rgba(148, 22, 127, 0.3)'
                  }}
                  popupClassName="temperature-select-dropdown"
                  style={{ color: themeStyles.textPrimary }}
                >
                  <Select.Option value={0} label="0.0 - Factual">0.0 - Factual</Select.Option>
                  <Select.Option value={0.2} label="0.2 - Precise">0.2 - Precise</Select.Option>
                  <Select.Option value={0.4} label="0.4 - Balanced">0.4 - Balanced</Select.Option>
                  <Select.Option value={0.6} label="0.6 - Natural">0.6 - Natural</Select.Option>
                  <Select.Option value={0.8} label="0.8 - Creative">0.8 - Creative</Select.Option>
                  <Select.Option value={1} label="1.0 - Most Creative">1.0 - Most Creative</Select.Option>
                </Select>
              </Form.Item>
            </div>
            {/* API key taken from backend environment; no user input required */}
          </>
        );
      }
      case STEP_KEYS.GRAPH_RAG:
        return (
          <>
            {renderStepHeading('Graph RAG')}
            <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '8px', borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                Optionally connect a Neo4j GraphRAG backend to build a knowledge graph from your documents. This enables relationship-aware queries alongside vector search.
              </p>
            </div>
            <Form.Item
              label="Enable Graph RAG"
              name={['config', 'graphrag', 'enabled']}
              initialValue={false}
              valuePropName="checked"
            >
              <Switch onChange={(checked) => {
                if (checked) {
                  const current = form.getFieldValue(['config', 'graphrag']) || {};
                  form.setFieldsValue({
                    config: {
                      ...form.getFieldValue('config'),
                      graphrag: {
                        ...current,
                        enabled: true,
                        ingest_endpoint: current.ingest_endpoint || 'https://backend.graphrag.thbscoetg.com/ingest',
                        timeout: current.timeout || 300,
                      }
                    }
                  });
                }
              }} />
            </Form.Item>
            {graphragEnabled && (
              <>
                <Form.Item
                  label="Ingest Endpoint"
                  name={['config', 'graphrag', 'ingest_endpoint']}
                  initialValue="https://backend.graphrag.thbscoetg.com/ingest"
                  rules={[{ required: true, message: 'Please enter the GraphRAG ingest endpoint' }]}
                >
                  <Input placeholder="https://backend.graphrag.thbscoetg.com/ingest" />
                </Form.Item>
                <Form.Item
                  label={<FieldLabel label="Group ID" tooltip="Logical grouping for documents in the knowledge graph. Documents in the same group can reference each other. Defaults to project ID if empty." />}
                  name={['config', 'graphrag', 'group_id']}
                >
                  <Input placeholder="Leave empty to use project ID" />
                </Form.Item>
                <Form.Item
                  label={<FieldLabel label="Timeout (seconds)" tooltip="Maximum time to wait for the GraphRAG service to process a document" />}
                  name={['config', 'graphrag', 'timeout']}
                  initialValue={300}
                >
                  <InputNumber min={30} max={900} style={{ width: '100%' }} />
                </Form.Item>
              </>
            )}
          </>
        );
      case STEP_KEYS.GUARDRAILS:
        return (
          <>
            {renderStepHeading('Guardrails')}
            <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '8px', borderLeft: '3px solid rgba(148, 22, 127, 0.5)' }}>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                Add safety layers to validate and filter both user inputs and AI outputs. Guardrails help prevent harmful, inappropriate, or sensitive content.
              </p>
            </div>
            <Form.Item label="Enable Guardrails" name={['config', 'guardrails', 'enabled']} initialValue={false} valuePropName="checked" style={{ marginTop: '24px' }}>
              <Switch />
            </Form.Item>
            
            {guardrailsEnabled && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '24px' }}>
                  <div style={{ padding: '16px', background: 'rgba(148, 22, 127, 0.04)', borderRadius: '8px', border: '1px solid rgba(148, 22, 127, 0.15)' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: themeStyles.textPrimary, fontSize: '15px' }}>Input Guards</h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: themeStyles.textTertiary }}>Applied to user chat queries before they reach the LLM.</p>
                    <Form.Item name={['config', 'guardrails', 'input_guards']} initialValue={[]} style={{ marginBottom: 0 }}>
                      <Select mode="multiple" placeholder="Select input guards" onChange={setSelectedInputGuards}>
                        <Select.Option value="DetectPII">Detect PII</Select.Option>
                        <Select.Option value="RestrictToTopic">Restrict To Topic</Select.Option>
                        <Select.Option value="NSFWText">NSFW Text</Select.Option>
                      </Select>
                    </Form.Item>
                    {selectedInputGuards.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        {selectedInputGuards.map(guard => renderGuardConfig(guard, 'input'))}
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '16px', background: 'rgba(148, 22, 127, 0.04)', borderRadius: '8px', border: '1px solid rgba(148, 22, 127, 0.15)' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: themeStyles.textPrimary, fontSize: '15px' }}>Pre-Embedding Guards</h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: themeStyles.textTertiary }}>Applied to document content before chunking. Use on_fail=fix to mask PII and continue ingestion.</p>
                    <Form.Item name={['config', 'guardrails', 'pre_embedding_guards']} initialValue={[]} style={{ marginBottom: 0 }}>
                      <Select mode="multiple" placeholder="Select pre-embedding guards" onChange={setSelectedPreEmbeddingGuards}>
                        <Select.Option value="DetectPII">Detect PII</Select.Option>
                        <Select.Option value="RestrictToTopic">Restrict To Topic</Select.Option>
                        <Select.Option value="NSFWText">NSFW Text</Select.Option>
                      </Select>
                    </Form.Item>
                    {selectedPreEmbeddingGuards.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        {selectedPreEmbeddingGuards.map(guard => renderGuardConfig(guard, 'pre_embedding'))}
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '16px', background: 'rgba(148, 22, 127, 0.04)', borderRadius: '8px', border: '1px solid rgba(148, 22, 127, 0.15)' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: themeStyles.textPrimary, fontSize: '15px' }}>Context (Retrieval) Guards</h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: themeStyles.textTertiary }}>Applied to each retrieved chunk after search, before it is wrapped as LLM context. Chunks that fail (e.g. block) are omitted from context.</p>
                    <Form.Item name={['config', 'guardrails', 'context_guards']} initialValue={[]} style={{ marginBottom: 0 }}>
                      <Select
                        mode="multiple"
                        placeholder="Select context (retrieval) guards"
                        onChange={(values) => {
                          setSelectedContextGuards(values);
                          const currentConfig = form.getFieldValue(['config', 'guardrails', 'context_guards_config']) || {};
                          const updatedConfig = { ...currentConfig };
                          values.forEach((guardName) => {
                            if (!updatedConfig[guardName]) updatedConfig[guardName] = {};
                            updatedConfig[guardName].on_fail = guardName === 'DetectPII' ? 'fix' : 'exception';
                            if (guardName === 'DetectPII') {
                              updatedConfig[guardName].entities = ['EMAIL_ADDRESS', 'PHONE_NUMBER'];
                              updatedConfig[guardName].pii_score_threshold = 0.7;
                            }
                          });
                          Object.keys(updatedConfig).forEach((guardName) => {
                            if (!values.includes(guardName)) delete updatedConfig[guardName];
                          });
                          form.setFieldValue(['config', 'guardrails', 'context_guards_config'], updatedConfig);
                        }}
                      >
                        <Select.Option value="DetectPII">Detect PII</Select.Option>
                        <Select.Option value="RestrictToTopic">Restrict To Topic</Select.Option>
                        <Select.Option value="NSFWText">NSFW Text</Select.Option>
                      </Select>
                    </Form.Item>
                    {selectedContextGuards.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        {selectedContextGuards.map((guard) => renderGuardConfig(guard, 'context'))}
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '16px', background: 'rgba(148, 22, 127, 0.04)', borderRadius: '8px', border: '1px solid rgba(148, 22, 127, 0.15)' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: themeStyles.textPrimary, fontSize: '15px' }}>Output Guards</h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: themeStyles.textTertiary }}>Applied to LLM responses before they are shown to the user.</p>
                    <Form.Item name={['config', 'guardrails', 'output_guards']} initialValue={[]} style={{ marginBottom: 0 }}>
                      <Select mode="multiple" placeholder="Select output guards" onChange={setSelectedOutputGuards}>
                        <Select.Option value="DetectPII">Detect PII</Select.Option>
                        <Select.Option value="RestrictToTopic">Restrict To Topic</Select.Option>
                        <Select.Option value="NSFWText">NSFW Text</Select.Option>
                      </Select>
                    </Form.Item>
                    {selectedOutputGuards.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        {selectedOutputGuards.map(guard => renderGuardConfig(guard, 'output'))}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <Button 
                    type="primary"
                    onClick={() => setTestModalVisible(true)}
                    style={{
                      background: 'linear-gradient(135deg, #94167F 0%, #153CB4 100%)',
                      border: 'none'
                    }}
                  >
                    Try it
                  </Button>
                </div>
              </>
            )}
          </>
        )
      case STEP_KEYS.REVIEW:
        const values = form.getFieldsValue(true);
        return (
          <>
            {renderStepHeading('Review', true)}
            <div style={{ marginBottom: '16px', color: themeStyles.textSecondary, fontStyle: 'italic' }}>
              Please review the information below. Click on a section to modify it.
              {knowledgeProvider === KNOWLEDGE_PROVIDER.S3 && !s3Verified && (
                <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
                  Knowledge Source settings are not verified yet. Verify S3 in the Knowledge Source step before you submit.
                </div>
              )}
              {knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE && !confluenceVerified && (
                <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
                  Confluence is not connected yet. Connect in the Knowledge Source step before you submit.
                </div>
              )}
              {knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB && !githubVerified && (
                <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
                  GitHub is not connected yet. Connect in the Knowledge Source step before you submit.
                </div>
              )}
              {knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA && !jiraVerified && (
                <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
                  Jira is not verified yet. Verify connection in the Knowledge Source step before you submit.
                </div>
              )}
              {knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI && !azureWikiVerified && (
                <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
                  Azure Wiki is not connected yet. Connect in the Knowledge Source step before you submit.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
            
            <div className="review-section" onClick={() => handleStepChange(getStepIndex(STEP_KEYS.PROJECT_DETAILS))} style={{ order: 1, marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>Project Details</h4>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Group">{groups.find((g) => g._id === values.group_id)?.name ?? values.group_id ?? 'Not set'}</Descriptions.Item>
                <Descriptions.Item label="Project Name">{values.name || 'Not set'}</Descriptions.Item>
                <Descriptions.Item label="Description">{values.description || 'Not set'}</Descriptions.Item>
              </Descriptions>
            </div>


            <div className="review-section" onClick={() => handleStepChange(getStepIndex(STEP_KEYS.EMBEDDING))} style={{ order: 4, marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>Embedding</h4>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Embedding Model">{values.config?.embedding?.model || 'openai'}</Descriptions.Item>
                <Descriptions.Item label="Model Name">{values.config?.embedding?.model_name || 'text-embedding-3-small'}</Descriptions.Item>
                {/* API key comes from backend environment; not shown here */}
              </Descriptions>
            </div>

            <div className="review-section" onClick={() => handleStepChange(getStepIndex(STEP_KEYS.VECTOR_STORE))} style={{ order: 5, marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>Vector Store</h4>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Vector Store Type">{values.config?.vector_store?.type || 'milvus'}</Descriptions.Item>
                <Descriptions.Item label="Collection Name">{values.config?.vector_store?.collection_name || 'custom_collection'}</Descriptions.Item>
              </Descriptions>
            </div>

            <div className="review-section" onClick={() => handleStepChange(getStepIndex(STEP_KEYS.KNOWLEDGE_SOURCE))} style={{ order: 2, marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>Knowledge Source</h4>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Provider">
                  {knowledgeProvider === KNOWLEDGE_PROVIDER.S3 && 'Amazon S3'}
                  {knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB && 'GitHub'}
                  {knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE && 'Confluence'}
                  {knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA && 'Jira'}
                  {knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI && 'Azure Wiki'}
                </Descriptions.Item>
                {knowledgeProvider === KNOWLEDGE_PROVIDER.S3 ? (
                  <>
                <Descriptions.Item label="Knowledge Source Name">
                  {values.config?.s3?.bucket || 'Not set'}
                </Descriptions.Item>
                <Descriptions.Item label="S3 Region">
                  {values.config?.s3?.region || 'Not set'}
                </Descriptions.Item>
                <Descriptions.Item label="Verification Status">
                  {s3Verified ? (
                    <span style={{ color: '#52c41a' }}>Verified</span>
                  ) : (
                    <span style={{ color: '#ff4d4f' }}>Not verified – go to "Knowledge Source" step and click Verify.</span>
                  )}
                </Descriptions.Item>
                  </>
                ) : knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE ? (
                  <>
                    <Descriptions.Item label="Wiki Base URL">
                      {values.config?.confluence?.base_url || 'Not set'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Account Email">
                      {values.config?.confluence?.email || 'Not set'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Verification Status">
                      {confluenceVerified ? (
                        <span style={{ color: '#52c41a' }}>Connected</span>
                      ) : (
                        <span style={{ color: '#ff4d4f' }}>Not connected – go to Knowledge Source and click Connect.</span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Space & folders">
                      Configure in RAG Pipeline → Source Settings after project creation
                    </Descriptions.Item>
                  </>
                ) : knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB ? (
                  <>
                    <Descriptions.Item label="GitHub base URL">
                      {values.config?.github?.github_base_url || 'Not set'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Verification Status">
                      {githubVerified ? (
                        <span style={{ color: '#52c41a' }}>Connected</span>
                      ) : (
                        <span style={{ color: '#ff4d4f' }}>Not connected – go to Knowledge Source and click Connect.</span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Repositories, branches, folders, file patterns">
                      Configure in RAG Pipeline → Source Settings after project creation
                    </Descriptions.Item>
                  </>
                ) : knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA ? (
                  <>
                    <Descriptions.Item label="Jira Base URL">
                      {values.config?.jira?.jira_base_url || 'Not set'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Project Keys and Scope">
                      Configure in RAG Pipeline → Source Settings after project creation
                    </Descriptions.Item>
                    <Descriptions.Item label="Account Email">
                      {values.config?.jira?.jira_user_email || 'Not set'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Verification Status">
                      {jiraVerified ? (
                        <span style={{ color: '#52c41a' }}>Verified</span>
                      ) : (
                        <span style={{ color: '#ff4d4f' }}>Not verified – go to Knowledge Source and click Verify.</span>
                      )}
                    </Descriptions.Item>
                  </>
                ) : knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI ? (
                  <>
                    <Descriptions.Item label="Organization URL">
                      {values.config?.azure_wiki?.organization_url || 'Not set'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Verification Status">
                      {azureWikiVerified ? (
                        <span style={{ color: '#52c41a' }}>Connected</span>
                      ) : (
                        <span style={{ color: '#ff4d4f' }}>Not connected – go to Knowledge Source and click Connect.</span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Projects & wikis">
                      Configure in RAG Pipeline → Source Settings after project creation
                    </Descriptions.Item>
                  </>
                ) : null}
              </Descriptions>
            </div>

            <div className="review-section" onClick={() => handleStepChange(getStepIndex(STEP_KEYS.RETRIEVER))} style={{ order: 7, marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>Retriever</h4>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Top K">{values.config?.retriever?.top_k ?? 5}</Descriptions.Item>
                <Descriptions.Item label="Search Type">
                  {retrieverSearchTypeLabel(values.config?.retriever?.search_type)}
                </Descriptions.Item>
                {values.config?.retriever?.search_type === 'similarity' && (
                  <>
                    <Descriptions.Item label="Cosine Similarity Threshold">{values.config?.retriever?.score_threshold ?? 'Not set'}</Descriptions.Item>
                    <Descriptions.Item label="Use Cosine Similarity">
                      <Tag color={values.config?.retriever?.cosine_similarity === false ? 'error' : 'success'}>
                        {values.config?.retriever?.cosine_similarity === false ? 'No' : 'Yes'}
                      </Tag>
                    </Descriptions.Item>
                  </>
                )}
                {values.config?.retriever?.search_type === 'mmr' && (
                  <>
                    <Descriptions.Item label="MMR Fetch K">{values.config?.retriever?.fetch_k ?? 20}</Descriptions.Item>
                    <Descriptions.Item label="MMR Lambda">{values.config?.retriever?.lambda_mult ?? 0.5}</Descriptions.Item>
                  </>
                )}
                {values.config?.retriever?.search_type === 'hybrid' && (
                  <>
                    <Descriptions.Item label="BM25 K">{values.config?.retriever?.bm25_k ?? 6}</Descriptions.Item>
                    <Descriptions.Item label="Vector K">{values.config?.retriever?.vector_k ?? 6}</Descriptions.Item>
                    <Descriptions.Item label="Hybrid Alpha">{values.config?.retriever?.hybrid_alpha ?? 0.6}</Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </div>

            <div className="review-section" onClick={() => handleStepChange(getStepIndex(STEP_KEYS.LLM))} style={{ order: 3, marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>LLM</h4>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="LLM Provider">{values.config?.llm?.provider || 'openai'}</Descriptions.Item>
                <Descriptions.Item label="LLM Model">{values.config?.llm?.model || 'gpt-4o-mini'}</Descriptions.Item>
                <Descriptions.Item label="Temperature">{values.config?.llm?.temperature ?? 0.6}</Descriptions.Item>
              </Descriptions>
            </div>

            <div className="review-section" onClick={() => handleStepChange(getStepIndex(STEP_KEYS.GRAPH_RAG))} style={{ order: 6, marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>Graph RAG</h4>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Graph RAG Enabled">
                  <Tag color={values.config?.graphrag?.enabled ? 'success' : 'error'}>
                    {values.config?.graphrag?.enabled ? 'Yes' : 'No'}
                  </Tag>
                </Descriptions.Item>
                {values.config?.graphrag?.enabled && (
                  <>
                    <Descriptions.Item label="Ingest Endpoint">{values.config?.graphrag?.ingest_endpoint || 'Not set'}</Descriptions.Item>
                    <Descriptions.Item label="Group ID">{values.config?.graphrag?.group_id || 'Project ID'}</Descriptions.Item>
                    <Descriptions.Item label="Timeout">{values.config?.graphrag?.timeout ?? 300} seconds</Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </div>

            <div className="review-section" onClick={() => handleStepChange(getStepIndex(STEP_KEYS.GUARDRAILS))} style={{ order: 8, marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', color: themeStyles.textPrimary }}>Guardrails</h4>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Guardrails Enabled">
                  <Tag color={values.config?.guardrails?.enabled ? 'success' : 'error'}>
                    {values.config?.guardrails?.enabled ? 'Yes' : 'No'}
                  </Tag>
                </Descriptions.Item>
                {values.config?.guardrails?.enabled ? (
                  <>
                <Descriptions.Item label="Input Guards">
                  {values.config?.guardrails?.input_guards?.length > 0 ? (
                    <div>
                      {values.config.guardrails.input_guards.map(guard => (
                        <div key={guard} style={{ marginBottom: '8px' }}>
                          <Tag color="blue">{guard}</Tag>
                          <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                            {Object.entries(values.config?.guardrails?.input_guards_config?.[guard] || {}).map(([key, value]) => (
                              <div key={key} style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                {key}: {JSON.stringify(value)}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : 'None'}
                </Descriptions.Item>
                <Descriptions.Item label="Output Guards">
                  {values.config?.guardrails?.output_guards?.length > 0 ? (
                    <div>
                      {values.config.guardrails.output_guards.map(guard => (
                        <div key={guard} style={{ marginBottom: '8px' }}>
                          <Tag color="purple">{guard}</Tag>
                          <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                            {Object.entries(values.config?.guardrails?.output_guards_config?.[guard] || {}).map(([key, value]) => (
                              <div key={key} style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                {key}: {JSON.stringify(value)}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : 'None'}
                </Descriptions.Item>
                <Descriptions.Item label="Pre-Embedding Guards">
                  {values.config?.guardrails?.pre_embedding_guards?.length > 0 ? (
                    <div>
                      {values.config.guardrails.pre_embedding_guards.map(guard => (
                        <div key={guard} style={{ marginBottom: '8px' }}>
                          <Tag color="gold">{guard}</Tag>
                          <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                            {Object.entries(values.config?.guardrails?.pre_embedding_guards_config?.[guard] || {}).map(([key, value]) => (
                              <div key={key} style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                {key}: {JSON.stringify(value)}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : 'None'}
                </Descriptions.Item>
                <Descriptions.Item label="Context (Retrieval) Guards">
                  {values.config?.guardrails?.context_guards?.length > 0 ? (
                    <div>
                      {values.config.guardrails.context_guards.map((guard) => (
                        <div key={guard} style={{ marginBottom: '8px' }}>
                          <Tag color="cyan">{guard}</Tag>
                          <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                            {Object.entries(values.config?.guardrails?.context_guards_config?.[guard] || {}).map(([key, value]) => (
                              <div key={key} style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                {key}: {JSON.stringify(value)}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    'None'
                  )}
                </Descriptions.Item>
                  </>
                ) : (
                  <Descriptions.Item label="Configured Guards">Hidden because Guardrails is disabled.</Descriptions.Item>
                )}
              </Descriptions>
            </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const handleTestGuardrail = async (text, filterType = 'input') => {
    if (!text.trim()) return;
    
    const values = form.getFieldsValue(true);
    const inputGuards = (values.config?.guardrails?.input_guards || []).map(guardName => ({
      guard_name: guardName,
      parameters: values.config?.guardrails?.input_guards_config?.[guardName] || {}
    }));

    const outputGuards = (values.config?.guardrails?.output_guards || []).map(guardName => ({
      guard_name: guardName,
      parameters: values.config?.guardrails?.output_guards_config?.[guardName] || {}
    }));

    const guardrailConfig = {
      enabled: values.config?.guardrails?.enabled || false,
      input_guards: inputGuards,
      output_guards: outputGuards
    };

    const userMessage = { type: 'human', text: text };
    setTestMessages([...testMessages, userMessage]);
    setTestingInput(true);
    setTestInputValue('');

    try {
      const response = await API.testGuardrails(guardrailConfig, text, filterType);
      const result = response.data;
      
      setTestMessages(prev => [...prev, { 
        type: 'ai', 
        text: result.passed ? '✅ Query passed all guardrails' : '❌ Query failed guardrail validation',
        passed: result.passed,
        violations: result.violations || [],
        filtered_text: result.filtered_text,
        original_text: result.original_text
      }]);
    } catch (error) {
      console.error('Error testing guardrails:', error);
      setTestMessages(prev => [...prev, { 
        type: 'ai', 
        text: 'Sorry, there was an error testing the guardrails.',
        passed: false
      }]);
    } finally {
      setTestingInput(false);
    }
  };

  useEffect(() => {
    const loadGroups = async () => {
      if (!token || !isAdmin || isSuperAdmin) return;
      try {
        const res = await API.listGroups(token);
        const rows = Array.isArray(res.data) ? res.data : [];
        setGroups(rows);
        if (rows.length === 1) {
          // Auto-bind the new project to the admin's only group; no manual choice needed.
          form.setFieldsValue({ group_id: rows[0]._id });
        }
      } catch (e) {
        // Best-effort; keep form usable even if groups fail
        setGroups([]);
      }
    };
    loadGroups();
  }, [token, isAdmin, isSuperAdmin]);

  useEffect(() => {
    const loadModelCatalog = async () => {
      try {
        setModelCatalogLoading(true);
        const res = await API.getModelCatalog();
        const catalog = res.data;
        setModelCatalog(catalog);

        const defaultProvider = catalog?.defaults?.llm_provider || 'openai';
        const defaultModel = catalog?.defaults?.llm_model || 'gpt-4o-mini';

        setSelectedLlmProvider(defaultProvider);
        form.setFieldsValue({
          config: {
            ...(form.getFieldValue('config') || {}),
            llm: {
              ...(form.getFieldValue(['config', 'llm']) || {}),
              provider: defaultProvider,
              model: defaultModel,
            },
          },
        });
      } catch (e) {
        // If the catalog fails to load, fall back to hard-coded defaults
        setModelCatalog(null);
      } finally {
        setModelCatalogLoading(false);
      }
    };

    loadModelCatalog();
  }, [form]);

  const [submitting, setSubmitting] = useState(false);

  const formatApiErrorDetail = (detail) => {
    if (!detail) return 'Failed to create project';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => (typeof d === 'string' ? d : d?.msg || d?.message || null))
        .filter(Boolean);
      return msgs.length ? msgs.join(', ') : 'Failed to create project';
    }
    if (typeof detail === 'object') {
      return detail?.msg || detail?.message || 'Failed to create project';
    }
    return 'Failed to create project';
  };

  const submitDisabled =
    submitting ||
    s3Verifying ||
    confluenceVerifying ||
    githubVerifying ||
    jiraVerifying ||
    azureWikiVerifying ||
    (knowledgeProvider === KNOWLEDGE_PROVIDER.S3 && s3Dirty && !s3Verified) ||
    (knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE && confluenceDirty && !confluenceVerified) ||
    (knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB && githubDirty && !githubVerified) ||
    (knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA && jiraDirty && !jiraVerified) ||
    (knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI && azureWikiDirty && !azureWikiVerified);

  const handleSubmit = async () => {
    if (submitting) return;

    if (knowledgeProvider === KNOWLEDGE_PROVIDER.S3 && s3Dirty && !s3Verified) {
      message.error('Please verify S3 configuration before submitting.');
      return;
    }

    if (knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE && (!confluenceVerified || !confluenceCredentialId)) {
      message.error('Please verify Confluence configuration before submitting.');
      return;
    }

    if (knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB && (!githubVerified || !githubCredentialId)) {
      message.error('Please connect GitHub before submitting.');
      return;
    }

    if (knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA && (!jiraVerified || !jiraCredentialId)) {
      message.error('Please verify Jira configuration before submitting.');
      return;
    }
    if (knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI && (!azureWikiVerified || !azureWikiCredentialId)) {
      message.error('Please connect Azure Wiki before submitting.');
      return;
    }
    try {
      await form.validateFields(['name', 'group_id', 'description', ['config', 'vector_store', 'collection_name']]);
    } catch {
      // AntD will highlight the invalid fields; avoid crashing on rejected promise
      return;
    }

    const values = form.getFieldsValue(true);
      const inputGuards = (values.config?.guardrails?.input_guards || []).map(guardName => ({
        guard_name: guardName,
        parameters: values.config?.guardrails?.input_guards_config?.[guardName] || {}
      }));

      const outputGuards = (values.config?.guardrails?.output_guards || []).map(guardName => ({
        guard_name: guardName,
        parameters: values.config?.guardrails?.output_guards_config?.[guardName] || {}
      }));
      const preEmbeddingGuards = (values.config?.guardrails?.pre_embedding_guards || []).map(guardName => ({
        guard_name: guardName,
        parameters: values.config?.guardrails?.pre_embedding_guards_config?.[guardName] || {}
      }));
      const contextGuards = (values.config?.guardrails?.context_guards || []).map((guardName) => ({
        guard_name: guardName,
        parameters: values.config?.guardrails?.context_guards_config?.[guardName] || {},
      }));

      // UPDATED: Chunking config without strategy - strategy will be set per-document
      const config = {
        chunking: {
          // No strategy field - will be selected per-document
          chunk_size: values.config?.chunking?.chunk_size || 1500,
          chunk_overlap: values.config?.chunking?.chunk_overlap || 300,
        },
        embedding: values.config?.embedding || {},
        vector_store: values.config?.vector_store || {},
        retriever: values.config?.retriever || {},
        llm: values.config?.llm || {},
        guardrails: {
          enabled: values.config?.guardrails?.enabled || false,
          input_guards: inputGuards,
          pre_embedding_guards: preEmbeddingGuards,
          context_guards: contextGuards,
          output_guards: outputGuards
        },
        knowledge_provider: knowledgeProvider,
        // Per-project knowledge source
        s3: knowledgeProvider === KNOWLEDGE_PROVIDER.S3 ? (values.config?.s3 || {}) : undefined,
        confluence:
          knowledgeProvider === KNOWLEDGE_PROVIDER.CONFLUENCE
            ? {
                ...(confluenceNormalized || {}),
                ...(values.config?.confluence || {}),
                credential_id: confluenceCredentialId,
                verified_at: new Date().toISOString(),
              }
            : undefined,
        github:
          knowledgeProvider === KNOWLEDGE_PROVIDER.GITHUB
            ? {
                ...(githubNormalized || {}),
                ...(values.config?.github || {}),
                credential_id: githubCredentialId,
                verified_at: new Date().toISOString(),
              }
            : undefined,
        jira:
          knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA
            ? {
                ...(jiraNormalized || {}),
                ...(values.config?.jira || {}),
                credential_id: jiraCredentialId,
                scopes: [],
                verified_at: new Date().toISOString(),
              }
            : undefined,
        azure_wiki:
          knowledgeProvider === KNOWLEDGE_PROVIDER.AZURE_WIKI
            ? {
                ...(azureWikiNormalized || {}),
                ...(values.config?.azure_wiki || {}),
                credential_id: azureWikiCredentialId,
                verified_at: new Date().toISOString(),
              }
            : undefined,
        // GraphRAG/Neo4j configuration
        graphrag: values.config?.graphrag || { enabled: false },
      };
      if (config.confluence) {
        delete config.confluence.api_token;
        delete config.confluence.space_key;
        delete config.confluence.space_id;
        delete config.confluence.space_name;
        delete config.confluence.sync_scope;
      }
      if (config.github) {
        delete config.github.pat;
        delete config.github.owner;
        delete config.github.repo;
        delete config.github.default_branch;
        delete config.github.path_prefix;
      }
      if (config.jira) {
        delete config.jira.jira_api_token;
        delete config.jira.project_key;
        delete config.jira.jql_query;
        delete config.jira.integration_id;
      }
      if (config.azure_wiki) {
        delete config.azure_wiki.pat;
        delete config.azure_wiki.projects;
        delete config.azure_wiki.wikis;
        delete config.azure_wiki.scopes;
      }
      
      // Debug: log full payload including S3 details
      // eslint-disable-next-line no-console
      console.log(
        '[CreateProject] Payload being sent:',
        JSON.stringify(
          {
            name: values.name,
            description: values.description,
            group_id: values.group_id,
            config,
          },
          null,
          2,
        ),
      );
      
      try {
        setSubmitting(true);
        if (knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA) {
          message.loading({
            content: 'Creating project and preparing Jira sync…',
            key: 'jira-create-sync',
            duration: 0,
          });
        }
        const response = await API.createRbacProjectWithConfig(
          token,
          values.name,
          values.description,
          values.group_id,
          config
        );
        if (knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA) {
          message.destroy('jira-create-sync');
        }
        message.success('Project created successfully!');
        // Use underlying core project_id returned by RBAC API
        navigate(`/project/${response.data.project_id}?tab=file_pipeline`);
      } catch (error) {
        if (knowledgeProvider === KNOWLEDGE_PROVIDER.JIRA) {
          message.destroy('jira-create-sync');
        }
        console.error('Error creating project:', error);
        message.error(humanizeApiError(error, { context: ERROR_CONTEXT.PROJECT_CREATE }));
      } finally {
        setSubmitting(false);
      }
  };

  // Super admins cannot create projects; non-admins are blocked too
  if (!isAdmin || isSuperAdmin) {
    return (
      <div style={{ padding: '24px' }}>
        <Card style={{ maxWidth: 600, margin: '0 auto' }}>
          <h3 style={{ marginBottom: 8 }}>Access restricted</h3>
          <p style={{ marginBottom: 0 }}>
            {isSuperAdmin
              ? 'Super admins cannot create projects. Use group admins to create projects within their groups.'
              : 'Only group admins can create projects. You can view projects you are assigned to from the dashboard.'}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="create-project-page"
      style={{ padding: '24px', display: 'flex', gap: '24px', maxWidth: '1400px', margin: '0 auto', height: '100%', overflow: 'hidden' }}
    >
      <div style={{ width: '200px', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: '8px' }}>
        <h2 style={{ 
          margin: '0 0 24px 0', 
          fontSize: '20px', 
          fontWeight: '600', 
          color: themeStyles.textPrimary,
          textAlign: 'left'
        }}>
          Create Projects
        </h2>
        <Steps 
          className="create-project-steps"
          direction="vertical" 
          current={currentStep} 
          items={stepItems} 
          onChange={handleStepChange}
          style={{ height: '100%' }}
        />
      </div>
      <div
        style={
          theme === 'light'
            ? {
                background: themeStyles.elevatedBg,
                border: `1px solid ${themeStyles.shellDivider}`,
                borderRadius: 12,
                flex: 1,
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: themeStyles.shadowCard,
              }
            : {
                background: 'linear-gradient(135deg, rgba(148, 22, 127, 0.1) 0%, rgba(21, 60, 180, 0.1) 100%)',
                border: '1px solid rgba(148, 22, 127, 0.3)',
                borderRadius: '12px',
                flex: 1,
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              }
        }
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          <Form form={form} layout="vertical" preserve requiredMark>
            {renderStepContent()}
          </Form>
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${themeStyles.borderColor}`, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={prev} 
            disabled={currentStep === 0}
            style={{
              background: currentStep === 0
                ? (theme === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255,255,255,0.06)')
                : (theme === 'light' ? 'rgba(148, 22, 127, 0.12)' : 'rgba(148, 22, 127, 0.5)'),
              border: `1px solid ${themeStyles.borderColorLight}`,
              color: currentStep === 0 ? themeStyles.textTertiary : themeStyles.textPrimary,
            }}
          >
            Previous
          </Button>
          {currentStep < steps.length - 1 ? (
            <Button
              type="primary"
              className="create-project-primary-btn"
              onClick={next}
              disabled={nextDisabled}
            >
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              className="create-project-primary-btn"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitDisabled}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
      <Drawer
        title="Test Guardrails"
        placement="right"
        width={600}
        onClose={() => {
          setTestModalVisible(false);
          setTestMessages([]);
          setTestInputValue('');
        }}
        open={testModalVisible}
        style={{ background: themeStyles.elevatedBg }}
        bodyStyle={{ background: themeStyles.elevatedBg, padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {testMessages.length === 0 && (
            <div style={{ textAlign: 'center', color: themeStyles.textSecondary, marginTop: '40px' }}>
              Enter a query to test if it passes or fails the selected guardrails
            </div>
          )}
          {testMessages.map((msg, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: msg.type === 'human' ? 'flex-end' : 'flex-start',
                gap: '12px',
              }}
            >
              {msg.type === 'ai' && (
                <Avatar
                  icon={<RobotOutlined />}
                  style={{
                    background: 'linear-gradient(135deg, #153CB4 0%, #94167F 100%)',
                    flexShrink: 0,
                  }}
                  size={40}
                />
              )}
              <div
                style={{
                  maxWidth: '85%',
                  padding: '16px',
                  borderRadius: '8px',
                  background: msg.type === 'human' 
                    ? '#F62E97'
                    : msg.passed !== undefined 
                      ? (msg.passed ? 'rgba(57, 255, 20, 0.1)' : 'rgba(255, 77, 79, 0.1)')
                      : 'transparent',
                  border: msg.type === 'human' 
                    ? '1px solid rgba(148, 22, 127, 0.7)'
                    : msg.passed !== undefined
                      ? (msg.passed ? '1px solid rgba(57, 255, 20, 0.3)' : '1px solid rgba(255, 77, 79, 0.3)')
                      : 'none',
                  color: msg.type === 'human' ? themeStyles.textInverse : themeStyles.textPrimary,
                }}
              >
                {msg.type === 'human' ? (
                  msg.text
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: msg.violations?.length > 0 ? '12px' : '0' }}>
                      {msg.passed !== undefined && (
                        <>
                          {msg.passed ? (
                            <CheckCircleOutlined style={{ color: '#39ff14', fontSize: '18px' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
                          )}
                        </>
                      )}
                      <div>{msg.text}</div>
                    </div>
                    {msg.violations && msg.violations.length > 0 && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${themeStyles.borderColor}` }}>
                        <div style={{ fontSize: '13px', color: themeStyles.textSecondary, marginBottom: '8px', fontWeight: 'bold' }}>Violations:</div>
                        {msg.violations.map((violation, idx) => (
                          <div key={idx} style={{ fontSize: '12px', color: '#ff4d4f', marginBottom: '6px', paddingLeft: '8px' }}>
                            <div>• <strong>{violation.guard}</strong>: {violation.reason}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {msg.filtered_text && msg.filtered_text !== msg.original_text && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${themeStyles.borderColor}` }}>
                        <div style={{ fontSize: '13px', color: themeStyles.textSecondary, marginBottom: '8px', fontWeight: 'bold' }}>Filtered Text:</div>
                        <div style={{ fontSize: '12px', color: themeStyles.textSecondary, padding: '8px', background: theme === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(0, 0, 0, 0.2)', borderRadius: '4px' }}>
                          {msg.filtered_text}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              {msg.type === 'human' && (
                <Avatar
                  icon={<UserOutlined />}
                  style={{
                    background: 'linear-gradient(135deg, #F62E97 0%, #94167F 100%)',
                    flexShrink: 0,
                  }}
                  size={40}
                />
              )}
            </div>
          ))}
          {testingInput && (
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start' }}>
              <Avatar
                icon={<RobotOutlined />}
                style={{
                  background: 'linear-gradient(135deg, #153CB4 0%, #94167F 100%)',
                  flexShrink: 0,
                }}
                size={40}
              />
              <div
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: themeStyles.textPrimary,
                }}
              >
                <Spin />
              </div>
            </div>
          )}
        </div>
        <div style={{ 
          background: themeStyles.elevatedBg, 
          borderTop: `1px solid ${themeStyles.borderColor}`,
          padding: '16px',
          display: 'flex',
          gap: '8px'
        }}>
          <Input
            value={testInputValue}
            onChange={(e) => setTestInputValue(e.target.value)}
            onPressEnter={() => handleTestGuardrail(testInputValue, 'input')}
            placeholder="Enter a query to test..."
            disabled={testingInput}
            style={{
              background: 'rgba(148, 22, 127, 0.1)',
              border: '1px solid rgba(148, 22, 127, 0.3)',
              color: themeStyles.textPrimary
            }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => handleTestGuardrail(testInputValue, 'input')}
            disabled={!testInputValue.trim() || testingInput}
            style={{
              background: 'linear-gradient(135deg, #94167F 0%, #153CB4 100%)',
              border: 'none'
            }}
          >
            Test
          </Button>
        </div>
      </Drawer>
    </div>
  );
};

export default CreateProject;
